/* ============================================================
   kevlts - Maison - interactions
   Lightweight, dependency-free. Powers scroll reveals for the
   custom kevlts homepage sections (.kv-reveal), the showcase
   rotator, and the order-wide cart limit (max 7 units).
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
  }

  function initReveals(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('.kv-reveal:not(.is-in)');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealAll(els);
      return;
    }

    var io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- showcase product rotator ---------- */
  function initShowcase(root) {
    var scope = root || document;
    var blocks = scope.querySelectorAll('[data-kv-showcase]');
    blocks.forEach(function (el) {
      if (el.__kvInit) return;
      el.__kvInit = true;

      var slides = el.querySelectorAll('.kv-pslide');
      var dots = el.querySelectorAll('[data-dot]');
      if (slides.length < 2) return;

      var current = 0;
      var delay = parseInt(el.getAttribute('data-autoplay'), 10) || 0;
      var timer = null;

      function show(i) {
        current = (i + slides.length) % slides.length;
        slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === current); });
        dots.forEach(function (d, idx) { d.classList.toggle('is-on', idx === current); });
      }

      function play() {
        if (!delay || reduceMotion) return;
        stop();
        timer = setInterval(function () { show(current + 1); }, delay * 1000);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          show(parseInt(d.getAttribute('data-dot'), 10));
          play();
        });
      });

      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', play);

      show(0);
      play();
    });
  }

  /* ---------- cart limit: max 7 units per order ----------
     cart.item_count is the SUM of every line's quantity, so a single
     product x 7 and 7 products x 1 are both treated as 7.
     Change KV_CART_LIMIT below to adjust the ceiling.            */
  var KV_CART_LIMIT = 7;
  var KV_LIMIT_MESSAGE =
    'You can order a maximum of ' + KV_CART_LIMIT + ' items per order.';
  function kvPartialMessage(remaining) {
    return 'Only ' + remaining + (remaining === 1 ? ' more item' : ' more items') +
      ' can be added - the limit is ' + KV_CART_LIMIT + ' items per order.';
  }
  // Shared with the redesign's own add-to-cart paths (window.kvCart.add, the
  // cart drawer's "+"), which post to the Ajax Cart API without a form submit.
  window.kvCartLimit = { max: KV_CART_LIMIT, full: KV_LIMIT_MESSAGE, partial: kvPartialMessage };

  function kvCartRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* The limit notice must always render inline (like the theme's own
     add-to-cart errors) - never as a native window.alert popup.
     Two templates render the buy button and they disagree:
       snippets/buy-buttons.liquid       -> ref="addToCartTextError"
       blocks/_product-buy-button.liquid -> no ref, and its modifier class
                                            (hdt-form-message--error) has no
                                            styling outside .hdt-customer /
                                            .hdt-recipient-form, so it renders
                                            unstyled.
     kvErrorBox therefore accepts either markup, creates the box when a form
     ships with neither, and kvStyleErrorBox paints it from the theme's own
     --color-error-text so mobile and desktop match.                        */
  // The gift-card recipient form (snippets/gift-card-recipient-form.liquid)
  // reuses .hdt-form__message-wrapper and is rendered BEFORE the buy button's
  // own box, so plain querySelector would hand back the wrong element on gift
  // card products. Skip anything living inside hdt-recipient-form.
  function kvFindErrorBox(form) {
    var tagged = form.querySelector('[ref="addToCartTextError"]');
    if (tagged) return tagged;

    var candidates = form.querySelectorAll('.hdt-form__message-wrapper');
    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i].closest('hdt-recipient-form, .hdt-recipient-form')) {
        return candidates[i];
      }
    }

    return form.kvErrorBox || null;
  }

  function kvErrorBox(form) {
    var box = kvFindErrorBox(form);

    if (!box) {
      box = document.createElement('div');
      box.className = 'hdt-form__message-wrapper hdt-form__message--error';
      box.setAttribute('role', 'alert');
      box.setAttribute('hidden', '');
      box.innerHTML = '<span class="hdt-error-message"></span>';

      var anchor = form.querySelector('hdt-buy-buttons') ||
        form.querySelector('.hdt-product-form__buttons') ||
        form.querySelector('[type="submit"][name="add"]');

      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(box, anchor);
      else form.appendChild(box);

      form.kvErrorBox = box;
    }

    return box;
  }

  function kvStyleErrorBox(box) {
    // The block template's class is misspelled (hdt-form-message--error), so
    // add the styled one rather than relying on whichever markup we landed on.
    if (box.classList) box.classList.add('hdt-form__message--error');
    box.style.color = 'rgb(var(--color-error-text))';
    box.style.display = 'flex';
    box.style.alignItems = 'center';
    box.style.gap = '0.7rem';
  }

  function kvShowError(form, message) {
    var box = kvErrorBox(form);
    if (!box) return;
    var text = box.querySelector('.hdt-error-message');
    if (text) text.textContent = message;
    else box.textContent = message;
    kvStyleErrorBox(box);
    box.removeAttribute('hidden');
  }

  function kvHideError(form) {
    var box = kvFindErrorBox(form);
    if (box) box.setAttribute('hidden', '');
  }

  function kvRequestedQty(form) {
    var input = form.querySelector('[name="quantity"]');
    var n = input ? parseInt(input.value, 10) : 1;
    return !n || n < 1 ? 1 : n;
  }

  function kvIsAddToCartForm(form) {
    return (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  // Runs in the CAPTURE phase on document, so it fires before the theme's
  // own submit handler (ProductForm in global.min.js) and can cancel it.
  function kvOnSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM' || !kvIsAddToCartForm(form)) return;

    // Second pass: this submission was already checked and approved.
    if (form.kvApproved) { form.kvApproved = false; return; }

    event.preventDefault();
    event.stopImmediatePropagation();

    var qty = kvRequestedQty(form);
    var submitter = event.submitter || form.querySelector('[type="submit"][name="add"]');

    function letItThrough() {
      if (!form.requestSubmit) return; // very old browser: leave the cart alone
      form.kvApproved = true;
      if (submitter) form.requestSubmit(submitter);
      else form.requestSubmit();
    }

    fetch(kvCartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var inCart = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
        var remaining = KV_CART_LIMIT - inCart;

        if (remaining <= 0) {
          kvShowError(form, KV_LIMIT_MESSAGE);
        } else if (qty > remaining) {
          kvShowError(form, kvPartialMessage(remaining));
        } else {
          kvHideError(form);
          letItThrough();
        }
      })
      .catch(function () {
        // If the cart can't be read, never block a genuine sale.
        letItThrough();
      });
  }

  document.addEventListener('submit', kvOnSubmit, true);

  function boot() { initReveals(document); initShowcase(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-scan when sections are re-rendered in the Theme Editor.
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initShowcase(e.target);
  });
})();

/* ============================================================
   <kv-header> (T10) - scroll states from the Home-Split header:
   top while y < 40; moving more than 6px down past 160px hides it;
   moving up 40px in one go shows it compact (so a finger's small
   back-movement mid-swipe no longer flashes it in and out). Stays
   visible while it holds focus or
   a dialog is open. Hides the cart count at 0 and keeps the cart
   link's accessible name in step with the theme's <hdt-cart-count>.
   ============================================================ */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('kv-header')) return;

  class KvHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = Math.max(0, window.scrollY || 0);
      this.turnY = this.lastY;
      this.scrollDir = 0;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.onFocus = this.onFocus.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('focusin', this.onFocus);
      this.update();
      this.watchCount();
      this.initMega();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('focusin', this.onFocus);
      if (this.countObserver) this.countObserver.disconnect();
      if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      clearTimeout(this.megaTimer);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    onFocus() {
      if (this.dataset.state === 'hidden') this.dataset.state = 'compact';
    }

    update() {
      const y = Math.max(0, window.scrollY || 0);
      const dy = y - this.lastY;
      let state = this.dataset.state || 'top';
      if (y < 40) {
        state = 'top';
        this.turnY = y;
        this.scrollDir = 0;
      } else if (dy !== 0) {
        // distances count from where the scroll last changed direction
        const dir = dy > 0 ? 1 : -1;
        if (dir !== this.scrollDir) {
          this.scrollDir = dir;
          this.turnY = this.lastY;
        }
        const busy = !!this.openTrigger || this.contains(document.activeElement) || !!document.querySelector('dialog[open]');
        if (dir > 0 && y - this.turnY > 6 && y > 160 && !busy) state = 'hidden';
        else if (dir < 0 && this.turnY - y > 40) state = 'compact';
      }
      this.lastY = y;
      if (state !== this.dataset.state) {
        this.dataset.state = state;
        this.closeMega(false);
      }
    }

    initMega() {
      this.triggers = Array.prototype.slice.call(this.querySelectorAll('[data-mega]'));
      this.pops = Array.prototype.slice.call(this.querySelectorAll('[data-kv-pop]'));
      if (!this.triggers.length && !this.pops.length) return;
      this.openTrigger = null;

      // Account dropdown + ship-to list (T13): click only, same one-open rule.
      this.pops.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          clearTimeout(this.megaTimer);
          if (this.openTrigger === trigger) this.closeMega(false);
          else this.openMega(trigger);
        });
      });

      // Brands panel: hovering or focusing a brand shows its first product in the feature card;
      // the card goes back to the editor's feature when the panel closes.
      this.querySelectorAll('.kv-mega--brands').forEach((panel) => {
        const feat = panel.querySelector('[data-kv-feat]');
        if (!feat || !feat.querySelector('[data-kv-feat-img]')) return;
        const base = feat.innerHTML;
        const baseHref = feat.getAttribute('href');
        let warm = false;
        const show = (a) => {
          if (!a || !a.dataset.kvFeatSrc || feat.dataset.kvFeatOn === a.href) return;
          const img = feat.querySelector('[data-kv-feat-img]');
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = a.dataset.kvFeatSrc;
          img.classList.remove('is-swap');
          void img.offsetWidth;
          img.classList.add('is-swap');
          [['eyebrow', 'kvFeatEyebrow'], ['title', 'kvFeatTitle'], ['link', 'kvFeatLink']].forEach(([part, key]) => {
            const el = feat.querySelector('[data-kv-feat-' + part + ']');
            if (!el) return;
            el.textContent = a.dataset[key] || '';
            (part === 'link' ? el.parentElement : el).hidden = !a.dataset[key];
          });
          feat.setAttribute('href', a.getAttribute('href'));
          feat.dataset.kvFeatOn = a.href;
        };
        panel.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse') show(e.target.closest('[data-kv-feat-src]')); });
        panel.addEventListener('focusin', (e) => show(e.target.closest('[data-kv-feat-src]')));
        panel.kvFeatWarm = () => {
          if (warm) return;
          warm = true;
          panel.querySelectorAll('[data-kv-feat-src]').forEach((a) => { new Image().src = a.dataset.kvFeatSrc; });
        };
        panel.kvFeatReset = () => {
          if (!feat.dataset.kvFeatOn) return;
          feat.innerHTML = base;
          feat.setAttribute('href', baseHref);
          delete feat.dataset.kvFeatOn;
        };
      });

      this.triggers.forEach((trigger) => {
        // A trigger that is also a link (Women, Men: their landing page) follows it on a mouse click or Enter; hovering
        // opens its panel as for the others. Touch has no hover: the first tap opens the panel, a second tap follows.
        const isLink = trigger.tagName === 'A';
        let lastPointer = '';
        trigger.addEventListener('pointerdown', (e) => { lastPointer = e.pointerType; });
        trigger.addEventListener('click', (e) => {
          clearTimeout(this.megaTimer);
          if (isLink) {
            const tap = lastPointer === 'touch' || lastPointer === 'pen';
            lastPointer = '';
            if (!tap || this.openTrigger === trigger) return;
            e.preventDefault();
            this.openMega(trigger);
            return;
          }
          if (this.openTrigger === trigger) this.closeMega(false);
          else this.openMega(trigger);
        });
        if (isLink) {
          trigger.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowDown') return;
            e.preventDefault();
            this.openMega(trigger);
            const first = document.getElementById(trigger.getAttribute('aria-controls')).querySelector('a');
            if (first) first.focus();
          });
        }
        trigger.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.warmMega(trigger); // the panel's images load during the hover delay, not after it opens
          clearTimeout(this.megaTimer);
          if (this.openTrigger && this.openTrigger.hasAttribute('data-mega')) this.openMega(trigger);
          else this.megaTimer = setTimeout(() => this.openMega(trigger), 110);
        });
      });

      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && this.openTrigger) clearTimeout(this.megaTimer);
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.megaTimer);
        if (this.openTrigger && this.openTrigger.hasAttribute('data-mega')) {
          this.megaTimer = setTimeout(() => this.closeMega(false), 180);
        }
      });
      // Every mega panel (the Brands panel first had this): it stays open only while the mouse is over its link, the
      // panel's content, or the gap between them; beside the link in the bar or in the panel's empty margins it closes
      // after a moment (a short delay so a diagonal move from the link to the content does not close it).
      this.addEventListener('pointermove', (e) => {
        const trigger = this.openTrigger;
        if (e.pointerType !== 'mouse' || !trigger || !trigger.hasAttribute('data-mega')) return;
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        if (this.inMegaZone(e.clientX, e.clientY, trigger, panel)) {
          clearTimeout(this.zoneTimer);
          this.zoneTimer = 0;
        } else if (!this.zoneTimer) {
          this.zoneTimer = setTimeout(() => {
            this.zoneTimer = 0;
            if (this.openTrigger === trigger) this.closeMega(false);
          }, 150);
        }
      }, { passive: true });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openTrigger) {
          e.stopPropagation();
          this.closeMega(true);
          return;
        }
        // Ship-to listbox: arrows open it on the selected country and move between options;
        // Enter / Space submit the focused option (Shopify localization form).
        const loc = e.target.closest && e.target.closest('.kv-loc__btn[aria-haspopup="listbox"], .kv-loc__list[role="listbox"] [role="option"]');
        if (!loc || ['ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(e.key) === -1) return;
        e.preventDefault();
        let list = document.getElementById('kv-loc-list');
        if (loc.classList.contains('kv-loc__btn')) {
          this.openMega(loc);
          list = document.getElementById(loc.getAttribute('aria-controls'));
        }
        if (!list) return;
        const opts = Array.prototype.slice.call(list.querySelectorAll('[role="option"]'));
        if (!opts.length) return;
        let i = opts.indexOf(document.activeElement);
        if (i === -1) i = Math.max(0, opts.findIndex((o) => o.getAttribute('aria-selected') === 'true'));
        else if (e.key === 'ArrowDown') i = (i + 1) % opts.length;
        else if (e.key === 'ArrowUp') i = (i - 1 + opts.length) % opts.length;
        if (e.key === 'Home') i = 0;
        if (e.key === 'End') i = opts.length - 1;
        opts[i].focus();
      });
      this.addEventListener('focusout', (e) => {
        if (this.openTrigger && e.relatedTarget && !this.contains(e.relatedTarget)) this.closeMega(false);
      });
      this.addEventListener('click', (e) => {
        const other = e.target.closest('[aria-controls]:not([data-mega]):not([data-kv-pop])');
        if (other && this.openTrigger) this.closeMega(false);
      });
      this.onDocClick = (e) => {
        if (this.openTrigger && !this.contains(e.target)) this.closeMega(false);
      };
      document.addEventListener('click', this.onDocClick);
    }

    // A mega panel's live area: the link, the box around the panel's columns, tiles and feature cards, and the band
    // between the link and that box (the way down from the link to the content).
    inMegaZone(x, y, trigger, panel) {
      const pad = 12;
      const t = trigger.getBoundingClientRect();
      if (x >= t.left - pad && x <= t.right + pad && y >= t.top - pad && y <= t.bottom + pad) return true;
      let l = Infinity;
      let r = -Infinity;
      let top = Infinity;
      let bottom = -Infinity;
      panel.querySelectorAll('.kv-mega__body > *, .kv-mega__foot > *').forEach((c) => {
        const b = c.getBoundingClientRect();
        if (!b.width || !b.height) return;
        l = Math.min(l, b.left);
        r = Math.max(r, b.right);
        top = Math.min(top, b.top);
        bottom = Math.max(bottom, b.bottom);
      });
      if (l === Infinity) return true;
      if (x >= l - pad && x <= r + pad && y >= top - pad * 2 && y <= bottom + pad * 2) return true;
      return y > t.bottom && y < top && x >= Math.min(t.left, l) - pad && x <= Math.max(t.right, r) + pad;
    }

    // Lazy images in a hidden panel would only start loading once it shows: start them on the first hover or open.
    warmMega(trigger) {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel || panel.kvWarm) return;
      panel.kvWarm = true;
      panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    openMega(trigger) {
      clearTimeout(this.zoneTimer);
      this.zoneTimer = 0;
      if (this.openTrigger === trigger) return;
      this.closeMega(false);
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      this.warmMega(trigger);
      panel.hidden = false;
      if (panel.kvFeatWarm) panel.kvFeatWarm();
      trigger.setAttribute('aria-expanded', 'true');
      this.openTrigger = trigger;
      this.classList.add('is-mega-open');
      if (this.dataset.state === 'hidden') this.dataset.state = 'compact';
    }

    closeMega(returnFocus) {
      clearTimeout(this.megaTimer);
      clearTimeout(this.zoneTimer);
      this.zoneTimer = 0;
      const trigger = this.openTrigger;
      if (!trigger) return;
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
      if (panel && panel.kvFeatReset) panel.kvFeatReset();
      trigger.setAttribute('aria-expanded', 'false');
      this.openTrigger = null;
      this.classList.remove('is-mega-open');
      if (returnFocus) trigger.focus();
    }

    watchCount() {
      const count = this.querySelector('.kv-hdr__count');
      const link = this.querySelector('.kv-hdr__cart');
      if (!count || !link || !('MutationObserver' in window)) return;
      const sync = () => {
        const n = parseInt(count.textContent, 10) || 0;
        count.hidden = n === 0;
        const tpl = n === 1 ? link.dataset.countOne : link.dataset.countOther;
        if (tpl) link.setAttribute('aria-label', link.dataset.label + ', ' + tpl.replace('[count]', n));
      };
      this.countObserver = new MutationObserver(sync);
      this.countObserver.observe(count, { childList: true, characterData: true, subtree: true });
      sync();
    }
  }

  customElements.define('kv-header', KvHeader);

  // Ship-to countries that are not Shopify markets yet (kv-ship-options): choosing one sets the ship-to label and
  // the product delivery estimate (kv:ship); prices stay in the market currency. Choosing a market clears it.
  const shipApply = (code) => {
    const opt = code && document.querySelector('[data-kv-ship="' + code + '"]');
    if (!opt) return false;
    document.querySelectorAll('[data-kv-ship-cur]').forEach((el) => { el.textContent = opt.dataset.kvShipLabel; });
    document.querySelectorAll('.kv-loc__btn').forEach((b) => { if (opt.dataset.kvShipAria) b.setAttribute('aria-label', opt.dataset.kvShipAria); });
    document.querySelectorAll('[data-kv-ship-code]').forEach((o) => {
      const on = o.dataset.kvShipCode === code;
      o.classList.toggle('is-current', on);
      if (o.getAttribute('role') === 'option') o.setAttribute('aria-selected', on ? 'true' : 'false');
      else if (on) o.setAttribute('aria-current', 'true');
      else o.removeAttribute('aria-current');
    });
    return true;
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-kv-ship], [data-kv-ship-market]');
    if (!t) return;
    if (t.hasAttribute('data-kv-ship-market')) {
      try { window.localStorage.removeItem('kv:ship'); } catch (err) { /* storage blocked */ }
      return;
    }
    const code = t.dataset.kvShip;
    try { window.localStorage.setItem('kv:ship', code); } catch (err) { /* storage blocked */ }
    shipApply(code);
    const hdr = t.closest('kv-header');
    if (hdr && hdr.closeMega) hdr.closeMega(true);
    document.dispatchEvent(new CustomEvent('kv:ship', { detail: { code } }));
  });
  try {
    const stored = window.localStorage.getItem('kv:ship');
    if (stored && !shipApply(stored)) window.localStorage.removeItem('kv:ship');
  } catch (err) { /* storage blocked */ }
})();

/* ============================================================
   Search panel (T12) - opens <dialog id="kv-search"> from any
   [aria-controls="kv-search"] trigger (modal: focus trap, Escape,
   top layer), fetches predictive results from the Predictive Search
   API rendered by sections/kevlts-predictive-search.liquid, and
   returns focus to the trigger on close. Enter submits to /search.
   ============================================================ */
(function () {
  'use strict';

  var SECTION = 'kevlts-predictive-search';

  function init() {
    var dlg = document.getElementById('kv-search');
    if (!dlg || dlg.dataset.kvReady) return;
    dlg.dataset.kvReady = '1';

    var input = dlg.querySelector('#kv-search-input');
    var results = dlg.querySelector('[data-kv-search-results]');
    var start = dlg.querySelector('[data-kv-search-start]');
    var status = dlg.querySelector('[data-kv-search-status]');
    var clear = dlg.querySelector('[data-kv-search-clear]');
    var chips = dlg.querySelector('[data-kv-search-chips]');
    var url = dlg.dataset.predictiveUrl;
    var timer = null;
    var ctrl = null;
    var opener = null;
    var isOpen = false;

    function triggers() {
      return document.querySelectorAll('[aria-controls="kv-search"]');
    }

    function setExpanded(value) {
      Array.prototype.forEach.call(triggers(), function (t) { t.setAttribute('aria-expanded', value); });
    }

    function open(trigger) {
      if (dlg.open) return;
      opener = trigger || document.activeElement;
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      isOpen = true;
      document.documentElement.classList.add('kv-search-open');
      setExpanded('true');
      setTimeout(function () {
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }, 60);
    }

    function close() {
      if (!dlg.open) return;
      if (typeof dlg.close === 'function') dlg.close();
      else dlg.removeAttribute('open');
      onClosed();
    }

    // Runs right after our own close() and again (no-op) on the native
    // 'close' event, which also covers Escape.
    function onClosed() {
      if (!isOpen) return;
      isOpen = false;
      document.documentElement.classList.remove('kv-search-open');
      setExpanded('false');
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    }

    function reset() {
      if (ctrl) ctrl.abort();
      ctrl = null;
      results.hidden = true;
      results.innerHTML = '';
      if (start) start.hidden = false;
      status.textContent = '';
    }

    function render(q) {
      if (ctrl) ctrl.abort();
      ctrl = 'AbortController' in window ? new AbortController() : null;
      status.textContent = dlg.dataset.loading || '';
      var u = url + '?q=' + encodeURIComponent(q) +
        '&section_id=' + SECTION +
        '&resources[type]=product,collection' +
        '&resources[limit]=8' +
        '&resources[options][unavailable_products]=last';
      fetch(u, ctrl ? { signal: ctrl.signal } : {})
        .then(function (r) {
          if (!r.ok) throw new Error('Predictive search ' + r.status);
          return r.text();
        })
        .then(function (html) {
          if (input.value.trim() !== q) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var ps = doc.querySelector('.kv-ps');
          if (!ps) throw new Error('Predictive search: no results markup');
          results.innerHTML = '';
          results.appendChild(document.importNode(ps, true));
          var slot = results.querySelector('[data-kv-search-chips-slot]');
          if (slot && chips) slot.appendChild(chips.cloneNode(true));
          status.textContent = ps.getAttribute('data-status') || '';
          if (start) start.hidden = true;
          results.hidden = false;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          status.textContent = '';
          results.hidden = true;
          if (start) start.hidden = false;
        });
    }

    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[aria-controls="kv-search"]');
      if (!t || !dlg.isConnected) return;
      e.preventDefault();
      open(t);
    });

    dlg.addEventListener('close', function () { if (!dlg.open) onClosed(); });

    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-kv-search-close]')) { close(); return; }
      if (e.target.closest('[data-kv-search-clear]')) {
        input.value = '';
        input.dispatchEvent(new Event('input'));
        input.focus();
        return;
      }
      var term = e.target.closest('[data-kv-search-term]');
      if (term) {
        input.value = term.getAttribute('data-kv-search-term');
        input.dispatchEvent(new Event('input'));
        input.focus();
      }
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clear.hidden = !q;
      clearTimeout(timer);
      if (!q) { reset(); return; }
      timer = setTimeout(function () { render(q); }, 250);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Mobile menu drawer + account sheet (T14) - native modal dialogs
   #kv-menu (opened by [aria-controls="kv-menu"]) and #kv-acct-sheet.
   One menu level at a time (Back returns to the root); Escape,
   scrim and Close close it and return focus to the opener. Search
   and Account in the menu close the drawer first and then open the
   search panel / account sheet.
   ============================================================ */
(function () {
  'use strict';

  function modal(dlg, lockClass) {
    var opener = null;
    var isOpen = false;
    function open(from) {
      if (dlg.open) return;
      opener = from || document.activeElement;
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      isOpen = true;
      document.documentElement.classList.add(lockClass);
      setExpanded('true');
    }
    function setExpanded(v) {
      Array.prototype.forEach.call(document.querySelectorAll('[aria-controls="' + dlg.id + '"]'), function (t) {
        t.setAttribute('aria-expanded', v);
      });
    }
    function cleanup(returnFocus) {
      if (!isOpen) return;
      isOpen = false;
      document.documentElement.classList.remove(lockClass);
      setExpanded('false');
      if (returnFocus !== false && opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    }
    function close(returnFocus) {
      if (!dlg.open) return;
      if (typeof dlg.close === 'function') dlg.close();
      else dlg.removeAttribute('open');
      cleanup(returnFocus);
    }
    // Ignore a stale 'close' event that arrives after the dialog was reopened.
    dlg.addEventListener('close', function () { if (!dlg.open) cleanup(true); });
    return { open: open, close: close };
  }

  function init() {
    var menu = document.getElementById('kv-menu');
    if (!menu || menu.dataset.kvReady) return;
    menu.dataset.kvReady = '1';
    var sheetEl = document.getElementById('kv-acct-sheet');
    var m = modal(menu, 'kv-menu-open');
    var sheet = sheetEl ? modal(sheetEl, 'kv-menu-open') : null;
    var root = menu.querySelector('[data-kv-menu-root]');
    var current = null;

    function showRoot(focusRow) {
      if (current) {
        current.panel.hidden = true;
        current.row.setAttribute('aria-expanded', 'false');
      }
      root.hidden = false;
      if (focusRow && current) current.row.focus();
      current = null;
    }

    function headerTrigger(id) {
      var list = document.querySelectorAll('kv-header [aria-controls="' + id + '"]');
      for (var i = 0; i < list.length; i++) if (list[i].offsetParent !== null) return list[i];
      return list[0] || null;
    }

    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[aria-controls="kv-menu"]');
      if (!t || !menu.isConnected) return;
      e.preventDefault();
      showRoot(false);
      m.open(t);
      var closeBtn = menu.querySelector('.kv-menu__top [data-kv-menu-close]');
      if (closeBtn) closeBtn.focus();
    });

    menu.addEventListener('click', function (e) {
      if (e.target.closest('[data-kv-menu-close]')) { m.close(); return; }
      var row = e.target.closest('[data-kv-menu-open]');
      if (row) {
        var panel = document.getElementById(row.getAttribute('aria-controls'));
        if (!panel) return;
        root.hidden = true;
        panel.hidden = false;
        row.setAttribute('aria-expanded', 'true');
        current = { row: row, panel: panel };
        var back = panel.querySelector('[data-kv-menu-back]');
        if (back) back.focus();
        return;
      }
      if (e.target.closest('[data-kv-menu-back]')) { showRoot(true); return; }
      if (e.target.closest('[data-kv-menu-search]')) {
        m.close(false);
        var s = headerTrigger('kv-search');
        if (s) s.click();
        return;
      }
      if (e.target.closest('[data-kv-menu-account]') && sheet) {
        m.close(false);
        sheet.open(headerTrigger('kv-menu'));
        var c = sheetEl.querySelector('.kv-sheet__head [data-kv-sheet-close]');
        if (c) c.focus();
      }
    });

    if (sheetEl) {
      sheetEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-kv-sheet-close]')) sheet.close();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Drawer primitive (T16) - generic controller for dialog.kv-drawer.
   Any [aria-controls="<id>"] whose target is a .kv-drawer opens it
   as a native modal (focus trap, inert page, Escape). Close via
   [data-kv-drawer-close], the scrim or Escape; focus returns to the
   opener. API for later tasks: window.kvDrawer.open(id, opener),
   window.kvDrawer.close(id). Triggers for other targets (e.g. the
   theme's CartDrawer) are left alone.
   ============================================================ */
(function () {
  'use strict';

  var state = {};

  function drawer(id) {
    var el = document.getElementById(id);
    return el && el.classList.contains('kv-drawer') && el.tagName === 'DIALOG' ? el : null;
  }

  function setExpanded(id, value) {
    Array.prototype.forEach.call(document.querySelectorAll('[aria-controls="' + id + '"]'), function (t) {
      t.setAttribute('aria-expanded', value);
    });
  }

  function anyOpen() {
    return Object.keys(state).some(function (k) { return state[k] && state[k].open; });
  }

  function cleanup(dlg, returnFocus) {
    var st = state[dlg.id];
    if (!st || !st.open) return;
    st.open = false;
    setExpanded(dlg.id, 'false');
    if (!anyOpen()) document.documentElement.classList.remove('kv-drawer-open');
    if (returnFocus !== false && st.opener && typeof st.opener.focus === 'function') {
      try { st.opener.focus({ preventScroll: true }); } catch (e) { st.opener.focus(); }
    }
    st.opener = null;
  }

  function bind(dlg) {
    if (dlg.dataset.kvDrawerReady) return;
    dlg.dataset.kvDrawerReady = '1';
    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-kv-drawer-close]')) close(dlg.id);
    });
    // Escape: slide out like the other ways of closing instead of the native instant close.
    dlg.addEventListener('cancel', function (e) {
      e.preventDefault();
      close(dlg.id);
    });
    // Ignore a stale 'close' event that arrives after the drawer was reopened.
    dlg.addEventListener('close', function () { if (!dlg.open) cleanup(dlg, true); });
  }

  // The panel's exit animation, or 0 when motion is off (reduced motion / --kv-motion-scale: 0).
  function exitMs(dlg) {
    var panel = dlg.querySelector('.kv-drawer__panel');
    if (!panel || !window.getComputedStyle) return 0;
    var cs = window.getComputedStyle(panel);
    if (cs.animationName === 'none') return 0;
    return (parseFloat(cs.animationDuration) || 0) * 1000;
  }

  function finishClose(dlg) {
    var st = state[dlg.id];
    if (st && st.timer) { clearTimeout(st.timer); st.timer = null; }
    dlg.classList.remove('is-closing');
    if (typeof dlg.close === 'function') dlg.close();
    else dlg.removeAttribute('open');
    cleanup(dlg, true);
  }

  function open(id, opener) {
    var dlg = drawer(id);
    if (!dlg) return false;
    var st = state[id];
    // Reopened while sliding out: slide straight back in.
    if (dlg.open && dlg.classList.contains('is-closing')) {
      if (st && st.timer) { clearTimeout(st.timer); st.timer = null; }
      dlg.classList.remove('is-closing');
      return true;
    }
    if (dlg.open) return false;
    bind(dlg);
    state[id] = { open: true, opener: opener || document.activeElement, timer: null };
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
    document.documentElement.classList.add('kv-drawer-open');
    setExpanded(id, 'true');
    var target = dlg.querySelector('[data-kv-drawer-focus]') || dlg.querySelector('.kv-drawer__head [data-kv-drawer-close]');
    // preventScroll: focusing inside the off-screen panel must not scroll the dialog mid-slide.
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
    return true;
  }

  function close(id, returnFocus) {
    var dlg = drawer(id);
    if (!dlg || !dlg.open) return false;
    if (dlg.classList.contains('is-closing')) return true;
    var st = state[id];
    if (st && returnFocus === false) st.opener = null;
    dlg.classList.add('is-closing');
    var ms = exitMs(dlg);
    if (!ms) {
      finishClose(dlg);
      return true;
    }
    var panel = dlg.querySelector('.kv-drawer__panel');
    var done = function (e) {
      if (e && e.target !== panel) return;
      panel.removeEventListener('animationend', done);
      if (dlg.classList.contains('is-closing')) finishClose(dlg);
    };
    panel.addEventListener('animationend', done);
    if (st) st.timer = setTimeout(done, ms + 120);
    return true;
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[aria-controls]');
    if (!t) return;
    var id = t.getAttribute('aria-controls');
    if (!drawer(id)) return;
    e.preventDefault();
    open(id, t);
  });

  window.kvDrawer = { open: open, close: close };
})();

/* ============================================================
   <kv-cart-drawer> (T17) - cart drawer on the T16 primitive.
   Keeps the theme's cart contract so existing product forms work:
   it carries ref="hdt-cart" + a sectionId getter (product forms add
   it to /cart/add.js "sections"), listens to cart:update / cart:reload,
   re-renders from the bundled section HTML, fires cart:count:update
   (read by <hdt-cart-count> in the header) and opens / redirects per
   actionAfterATC like the old drawer. Quantity / remove use the Ajax
   Cart API /cart/change.js with bundled section rendering; the order
   note is saved with /cart/update.js.
   ============================================================ */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('kv-cart-drawer')) return;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, json: j }; });
    });
  }

  class KvCartDrawer extends HTMLElement {
    get sectionId() { return this.getAttribute('section-id'); }

    connectedCallback() {
      this.ac = new AbortController();
      const sig = { signal: this.ac.signal };
      document.addEventListener('cart:update', (e) => this.onUpdate(e), sig);
      document.addEventListener('cart:reload', () => this.reload(), sig);
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.reload(); }, sig);
      this.addEventListener('click', (e) => this.onClick(e), sig);
      this.addEventListener('change', (e) => {
        if (e.target.matches && e.target.matches('[data-kv-cart-note]')) this.saveNote(e.target.value);
      }, sig);
    }

    disconnectedCallback() { if (this.ac) this.ac.abort(); }

    // Theme code may call cart.open() on the [ref="hdt-cart"] element.
    open() { if (window.kvDrawer) window.kvDrawer.open('kv-cart'); }

    onUpdate(e) {
      const d = e.detail || {};
      const sections = d.cartData && d.cartData.sections;
      if (!sections || !sections[this.sectionId]) { this.reload(); return; }
      this.render(sections[this.sectionId]);
      if (d.source === 'product-form-component') {
        const settings = window.themeHDN && window.themeHDN.settings;
        const action = d.actionAfterATC || (settings && settings.actionAfterATC) || '';
        if (action.indexOf('go_') > -1) {
          window.location.href = root() + (action === 'go_cart_page' ? 'cart' : 'checkout');
        } else if (action === 'open_cart_drawer') {
          this.open();
        }
      }
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-kv-cart-content]');
      const cur = this.querySelector('[data-kv-cart-content]');
      if (!next || !cur) return false;
      cur.replaceWith(document.importNode(next, true));
      const count = parseInt(next.getAttribute('data-count'), 10) || 0;
      this.setAttribute('data-count', count);
      document.dispatchEvent(new CustomEvent('cart:count:update', { detail: { item_count: count } }));
      // The header count is a theme <hdt-cart-count>; update it directly if that element isn't defined.
      if (!customElements.get('hdt-cart-count')) {
        Array.prototype.forEach.call(document.querySelectorAll('hdt-cart-count'), function (el) { el.textContent = count; });
      }
      return true;
    }

    reload() {
      const url = window.location.pathname + '?sections=' + encodeURIComponent(this.sectionId);
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((j) => { if (j && j[this.sectionId]) this.render(j[this.sectionId]); })
        .catch(function () {});
    }

    onClick(e) {
      const btn = e.target.closest('[data-kv-cart-qty]');
      if (!btn) return;
      const item = btn.closest('[data-line]');
      if (!item) return;
      const kind = btn.classList.contains('kv-cart__remove') ? 'remove' : (btn === item.querySelectorAll('[data-kv-cart-qty]')[0] ? 'dec' : 'inc');
      this.change(parseInt(item.getAttribute('data-line'), 10), parseInt(btn.getAttribute('data-kv-cart-qty'), 10), item, kind);
    }

    change(line, quantity, item, kind) {
      if (this.busy) return Promise.resolve();
      if (kind === 'inc' && window.kvLimitMessage) {
        // the store's order-wide limit (kevlts.js top): "+" only when one more unit fits
        this.busy = true;
        item.classList.add('is-busy');
        return window.kvLimitMessage(1).then((msg) => {
          this.busy = false;
          if (!msg) return this.post(line, quantity, item, kind);
          item.classList.remove('is-busy');
          const status = this.querySelector('[data-kv-cart-status]');
          if (status) status.textContent = msg;
        });
      }
      return this.post(line, quantity, item, kind);
    }

    post(line, quantity, item, kind) {
      this.busy = true;
      item.classList.add('is-busy');
      return postJSON(root() + 'cart/change.js', {
        line: line,
        quantity: quantity,
        sections: [this.sectionId],
        sections_url: window.location.pathname
      }).then((res) => {
        const j = res.json || {};
        if (!res.ok || j.status || !j.sections || !j.sections[this.sectionId]) {
          item.classList.remove('is-busy');
          const status = this.querySelector('[data-kv-cart-status]');
          if (status) status.textContent = j.description || j.message || '';
          return;
        }
        this.render(j.sections[this.sectionId]);
        this.restoreFocus(line, quantity, kind);
      }).catch(() => {
        item.classList.remove('is-busy');
      }).then(() => { this.busy = false; });
    }

    restoreFocus(line, quantity, kind) {
      const row = this.querySelector('[data-line="' + line + '"]');
      let target = null;
      if (row && quantity > 0 && kind !== 'remove') {
        const btns = row.querySelectorAll('[data-kv-cart-qty]');
        target = kind === 'dec' ? btns[0] : btns[1];
      } else if (row) {
        target = row.querySelector('.kv-cart__name');
      } else {
        const rows = this.querySelectorAll('[data-line]');
        target = rows.length ? rows[rows.length - 1].querySelector('.kv-cart__name') : this.querySelector('.kv-drawer__head [data-kv-drawer-close]');
      }
      if (target) target.focus();
    }

    saveNote(note) {
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(function () {
        postJSON(root() + 'cart/update.js', { note: note }).catch(function () {});
      }, 300);
    }
  }

  customElements.define('kv-cart-drawer', KvCartDrawer);
})();

/* ============================================================
   Back-to-top + toasts (T18) - snippets/kv-overlays.liquid.
   Back-to-top: shown once scrolled past 0.85 x the viewport (after
   the hero); the ring fills with progress to the top of the footer
   (minus 0.65 x viewport), as in the Home-Split board; smooth scroll
   (instant under reduced motion). Toasts: window.kvToast.show({...})
   - type 'added' (image, name, View -> cart drawer) or 'confirm'
   (text); 4s, paused while hovered / focused. An added toast is shown
   after a theme product-form add when "after add to cart" is "no"
   (the default "open_cart_drawer" opens the drawer instead).
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function initTop() {
    var btn = document.querySelector('[data-kv-top]');
    if (!btn || btn.dataset.kvReady) return;
    btn.dataset.kvReady = '1';
    var ring = btn.querySelector('.kv-top__ring path');
    var ticking = false;
    function update() {
      ticking = false;
      var de = document.documentElement;
      var y = window.scrollY || de.scrollTop || 0;
      var V = window.innerHeight;
      var H = de.scrollHeight;
      var can = H > V + 4;
      var end = H - V;
      var ft = document.querySelector('.kv-ft');
      if (ft) end = Math.min(end, ft.getBoundingClientRect().top + y - V * 0.65);
      var k = can ? Math.max(0, Math.min(1, y / Math.max(1, end))) : 0;
      if (ring) ring.style.strokeDashoffset = String(100 - k * 100);
      btn.classList.toggle('is-on', can && y > V * 0.85);
      btn.classList.toggle('is-full', k >= 0.999);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    btn.kvUpdate = update;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    btn.addEventListener('click', function () {
      var smooth = !(reduce && reduce.matches);
      try { window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' }); } catch (e) { window.scrollTo(0, 0); }
      var target = document.querySelector('main, #MainContent') || document.body;
      if (target && target !== document.body) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    });
    update();
  }

  function region() { return document.querySelector('[data-kv-toasts]'); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function show(opts) {
    var r = region();
    if (!r || !opts) return null;
    var t = el('div', 'kv-toast kv-toast--' + (opts.type === 'confirm' ? 'confirm' : 'added'));
    if (opts.type === 'confirm') {
      t.textContent = opts.text || '';
    } else {
      if (opts.image) {
        var img = el('img', 'kv-toast__img');
        img.src = opts.image; img.alt = ''; img.width = 48; img.height = 48;
        t.appendChild(img);
      }
      var txt = el('span', 'kv-toast__text');
      txt.appendChild(el('span', 'kv-toast__eyebrow', opts.eyebrow || r.getAttribute('data-added-label') || ''));
      txt.appendChild(el('span', 'kv-toast__name', opts.title || ''));
      t.appendChild(txt);
      var view = el('a', 'kv-toast__view', r.getAttribute('data-view-label') || '');
      view.href = r.getAttribute('data-cart-url') || '/cart';
      if (document.getElementById('kv-cart')) view.setAttribute('aria-controls', 'kv-cart');
      view.addEventListener('click', function () { dismiss(t); });
      t.appendChild(view);
    }
    while (r.children.length >= 3) r.removeChild(r.firstElementChild);
    r.appendChild(t);
    var remaining = opts.duration || 4000;
    var started = Date.now();
    var timer = setTimeout(function () { dismiss(t); }, remaining);
    function pause() { clearTimeout(timer); remaining -= Date.now() - started; }
    function resume() {
      if (t.contains(document.activeElement)) return;
      started = Date.now();
      clearTimeout(timer);
      timer = setTimeout(function () { dismiss(t); }, Math.max(1000, remaining));
    }
    t.addEventListener('mouseenter', pause);
    t.addEventListener('focusin', pause);
    t.addEventListener('mouseleave', resume);
    t.addEventListener('focusout', function () { setTimeout(resume, 0); });
    t.kvTimer = function () { return timer; };
    return t;
  }

  function dismiss(t) {
    if (!t || !t.parentNode || t.classList.contains('is-leaving')) return;
    if (reduce && reduce.matches) { t.remove(); return; }
    t.classList.add('is-leaving');
    t.addEventListener('animationend', function () { t.remove(); }, { once: true });
    setTimeout(function () { if (t.parentNode) t.remove(); }, 400);
  }

  // Added-to-cart toast after a theme product-form add when the store stays on the page.
  document.addEventListener('cart:update', function (e) {
    var d = e.detail || {};
    if (d.source !== 'product-form-component') return;
    var settings = window.themeHDN && window.themeHDN.settings;
    var action = d.actionAfterATC || (settings && settings.actionAfterATC) || '';
    if (action === 'open_cart_drawer' || action.indexOf('go_') > -1) return;
    var data = d.cartData || {};
    var item = data.items && data.items.length ? data.items[0] : data;
    if (!item || !item.product_title) return;
    show({ type: 'added', title: item.product_title, image: item.image || (item.featured_image && item.featured_image.url) || '' });
  });

  window.kvToast = { show: show, dismiss: dismiss };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTop);
  else initTop();
  document.addEventListener('shopify:section:load', initTop);
})();

/* ============================================================
   Cards, quick view, facets, home sections (T20-T22, T30).
   Delegated on document so Theme Editor section reloads keep working.
   - [data-kv-card-add]: single-variant products are added with
     /cart/add.js (bundling the cart drawer section, then cart:update
     with source 'product-form-component', as theme product forms do);
     products with options go to their product page.
   - [data-kv-add-ids="id,id"]: adds one or more variants (look rows,
     "Add the full set", hotspot popovers).
   - .kv-pc__sw swatches swap the card's front image (hover previews,
     click selects).
   - [data-kv-quick="/products/x"]: quick-view modal (dialog#kv-quick),
     content from ?section_id=kevlts-quick-view-content; note rows are
     cloned from the shell's <template>.
   - [data-kv-facets]: sort list, 4/3 grid view, live "Show N" count.
   - [data-kv-tabs], [data-kv-hotspot], [data-kv-rail].
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function withParam(url, key, value) { return url + (url.indexOf('?') > -1 ? '&' : '?') + key + '=' + encodeURIComponent(value); }

  /* ---------- add to cart ---------- */
  /* Order-wide quantity limit (window.kvCartLimit, the store's own rule above): resolves
     null when `adding` more units fit, otherwise the store's limit message. */
  function limitMessage(adding) {
    var lim = window.kvCartLimit;
    if (!lim || !lim.max) return Promise.resolve(null);
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var remaining = lim.max - (c && typeof c.item_count === 'number' ? c.item_count : 0);
        if (adding <= remaining) return null;
        return remaining <= 0 ? lim.full : lim.partial(remaining);
      })
      .catch(function () { return null; }); // if the cart can't be read, never block a genuine sale
  }
  window.kvLimitMessage = limitMessage;

  function addItems(ids, btn) {
    if (!ids.length || (btn && btn.getAttribute('aria-busy') === 'true')) return Promise.resolve(null);
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    return limitMessage(ids.length).then(function (msg) {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
      if (!msg) return postItems(ids, btn);
      if (window.kvToast) window.kvToast.show({ type: 'confirm', text: msg });
      return null;
    });
  }

  function postItems(ids, btn) {
    var cart = document.querySelector('[ref="hdt-cart"]');
    var sectionId = cart && cart.sectionId;
    var body = { items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) };
    if (sectionId) body.sections = [sectionId];
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, json: j }; });
    }).then(function (res) {
      var j = res.json || {};
      if (!res.ok || j.status) {
        if (window.kvToast) window.kvToast.show({ type: 'confirm', text: j.description || j.message || '' });
        return null;
      }
      var settings = window.themeHDN && window.themeHDN.settings;
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { cartData: j, source: 'product-form-component', actionAfterATC: (settings && settings.actionAfterATC) || 'open_cart_drawer' }
      }));
      return j;
    }).catch(function () {
      document.dispatchEvent(new CustomEvent('cart:reload'));
      return null;
    }).then(function (j) {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
      return j;
    });
  }

  /* ---------- swatches ---------- */
  function frontOf(sw) {
    var card = sw.closest('[data-kv-card]');
    return card && card.querySelector('[data-kv-front]');
  }
  function showImage(sw) {
    var img = frontOf(sw);
    var src = sw.getAttribute('data-img');
    if (!img || !src) return;
    if (!img.dataset.kvSrc) { img.dataset.kvSrc = img.getAttribute('src'); img.dataset.kvSrcset = img.getAttribute('srcset') || ''; }
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
  }
  // back to the picked colour's image (kept after the pointer or focus leaves), or the card's own image
  function restoreImage(row) {
    var picked = row.querySelector('.kv-pc__sw.is-picked[data-img]');
    if (picked) { showImage(picked); return; }
    var card = row.closest('[data-kv-card]');
    var img = card && card.querySelector('[data-kv-front]');
    if (!img || !img.dataset.kvSrc) return;
    img.setAttribute('src', img.dataset.kvSrc);
    if (img.dataset.kvSrcset) img.setAttribute('srcset', img.dataset.kvSrcset);
  }
  // picking a colour keeps it: image, card links (?variant=) and Add to cart follow that variant
  function selectSwatch(sw) {
    qa('.kv-pc__sw', sw.closest('.kv-pc__swr')).forEach(function (s) {
      var on = s === sw;
      s.classList.toggle('is-on', on);
      s.classList.toggle('is-picked', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    restoreImage(sw.closest('.kv-pc__swr'));
    var card = sw.closest('[data-kv-card]');
    var url = sw.getAttribute('data-url');
    if (!card || !url) return;
    card.setAttribute('data-url', url);
    qa('[data-kv-card-link]', card).forEach(function (a) { a.setAttribute('href', url); });
    var add = card.querySelector('[data-kv-card-add]');
    if (!add) return;
    add.setAttribute('data-url', url);
    var vid = sw.getAttribute('data-variant-id');
    if (vid && card.hasAttribute('data-single-option')) {
      add.setAttribute('data-variant-id', vid);
      add.setAttribute('data-single', 'true');
      add.disabled = sw.getAttribute('data-available') !== 'true';
    }
  }
  document.addEventListener('mouseover', function (e) {
    var sw = e.target.closest && e.target.closest('.kv-pc__sw');
    if (sw) showImage(sw);
  });
  document.addEventListener('mouseout', function (e) {
    var row = e.target.closest && e.target.closest('.kv-pc__swr');
    if (row && !(e.relatedTarget && row.contains(e.relatedTarget))) restoreImage(row);
  });
  document.addEventListener('focusin', function (e) {
    var sw = e.target.closest && e.target.closest('.kv-pc__sw');
    if (sw) showImage(sw);
  });
  document.addEventListener('focusout', function (e) {
    var row = e.target.closest && e.target.closest('.kv-pc__swr');
    if (row && !(e.relatedTarget && row.contains(e.relatedTarget))) restoreImage(row);
  });

  /* ---------- quick view ---------- */
  var qv = { opener: null, token: 0 };
  function modal() { return document.getElementById('kv-quick'); }

  function qvCleanup(returnFocus) {
    var dlg = modal();
    if (!dlg || !dlg.kvOpen) return;
    dlg.kvOpen = false;
    qv.token++;
    document.documentElement.classList.remove('kv-modal-open');
    if (qv.opener && qv.opener.setAttribute) qv.opener.setAttribute('aria-expanded', 'false');
    if (returnFocus !== false && qv.opener && typeof qv.opener.focus === 'function' && document.contains(qv.opener)) qv.opener.focus();
    qv.opener = null;
  }

  function qvBind(dlg) {
    if (dlg.dataset.kvReady) return;
    dlg.dataset.kvReady = '1';
    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-kv-modal-close]')) { e.preventDefault(); qvClose(); return; }
      var add = e.target.closest('[data-kv-qv-add]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        addItems([add.getAttribute('data-variant-id')], add).then(function (j) { if (j) qvClose(false); });
      }
    });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); qvClose(); });
    // Ignore a stale 'close' event that arrives after the modal was reopened.
    dlg.addEventListener('close', function () { if (!dlg.open) qvCleanup(true); });
  }

  function qvOpen(url, opener) {
    var dlg = modal();
    if (!dlg || !url) return false;
    qvBind(dlg);
    var box = dlg.querySelector('[data-kv-qv-content]');
    if (dlg.open) qvClose(false);
    qv.opener = opener || document.activeElement;
    dlg.kvOpen = true;
    var token = ++qv.token;
    box.innerHTML = '';
    dlg.setAttribute('aria-busy', 'true');
    if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
    document.documentElement.classList.add('kv-modal-open');
    if (qv.opener && qv.opener.setAttribute) qv.opener.setAttribute('aria-expanded', 'true');
    var closeBtn = dlg.querySelector('.kv-modal__close');
    if (closeBtn) closeBtn.focus();
    fetch(withParam(url.split('#')[0], 'section_id', 'kevlts-quick-view-content'))
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        if (token !== qv.token) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-kv-qv]');
        if (!content) throw new Error('empty');
        box.innerHTML = '';
        box.appendChild(document.importNode(content, true));
        var tpl = dlg.querySelector('template[data-kv-qv-notes]');
        var slot = box.querySelector('[data-kv-qv-notes-slot]');
        if (tpl && slot) slot.replaceWith(tpl.content.cloneNode(true));
      })
      .catch(function () {
        if (token !== qv.token) return;
        qvClose(false);
        window.location.href = url;
      })
      .then(function () { if (token === qv.token) dlg.removeAttribute('aria-busy'); });
    return true;
  }

  function qvClose(returnFocus) {
    var dlg = modal();
    if (!dlg) return false;
    if (dlg.open) { if (typeof dlg.close === 'function') dlg.close(); else dlg.removeAttribute('open'); }
    qvCleanup(returnFocus);
    return true;
  }

  window.kvQuickView = { open: qvOpen, close: qvClose };
  // Shared add-to-cart (T33 product page): add(variantIds, button) -> Promise<cart JSON | null>.
  window.kvCart = { add: addItems };

  /* ---------- facets toolbar ---------- */
  function facetsForm() { return document.querySelector('[data-kv-facets-form]'); }
  function formUrl(form, extra) {
    var params = new URLSearchParams(new FormData(form));
    Object.keys(extra || {}).forEach(function (k) { params.set(k, extra[k]); });
    return form.getAttribute('action') + '?' + params.toString();
  }
  function closeSort(bar, focus) {
    var btn = bar && bar.querySelector('[data-kv-sort-toggle]');
    var list = btn && document.getElementById(btn.getAttribute('aria-controls'));
    if (!list || list.hidden) return;
    list.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (focus) btn.focus();
  }
  function setView(bar, cols) {
    qa('[data-kv-view]', bar).forEach(function (b) {
      var on = b.getAttribute('data-kv-view') === cols;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var scope = bar.closest('.shopify-section') || document;
    qa('.kv-grid', scope).forEach(function (g) { g.classList.toggle('kv-grid--3', cols === '3'); });
    try { window.localStorage.setItem('kv-grid-view', cols); } catch (e) { /* storage unavailable */ }
  }
  var countTimer = null;
  var countToken = 0;
  function liveCount(form) {
    var bar = document.querySelector('[data-kv-facets]');
    var sid = bar && bar.getAttribute('data-section-id');
    var submit = document.querySelector('[data-kv-facets-submit]');
    if (!sid || !submit) return;
    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
      var token = ++countToken;
      fetch(formUrl(form, { section_id: sid }))
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          if (token !== countToken) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var n = doc.querySelector('[data-kv-results-count]');
          if (!n) return;
          var count = n.textContent.trim();
          var label = submit.getAttribute('data-label');
          submit.textContent = label ? label.replace('[n]', count) : count;
        })
        .catch(function () { /* the plain GET submit still works */ });
    }, 250);
  }
  document.addEventListener('change', function (e) {
    var form = e.target.closest && e.target.closest('[data-kv-facets-form]');
    if (form) liveCount(form);
  });

  /* ---------- tabs ---------- */
  function selectTab(tab, focus) {
    var list = tab.closest('[role="tablist"]');
    qa('[role="tab"]', list).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) t.removeAttribute('tabindex'); else t.setAttribute('tabindex', '-1');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  }

  /* ---------- hotspots ---------- */
  // Board (Home-Split): a point or its row opens the popover; hover opens it with a mouse.
  // Phones (Home-Split-Mobile): no popover, the point highlights its swipe card and scrolls to it;
  // with nothing open the first card is highlighted.
  var lookPhone = window.matchMedia('(max-width: 760px)');
  function setSpot(btn, open) {
    var id = btn.getAttribute('aria-controls');
    var pop = document.getElementById(id);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) btn.removeAttribute('data-kv-hover-open');
    if (pop) pop.hidden = !open;
    var spot = btn.closest('.kv-look__spot');
    if (spot) spot.classList.toggle('is-open', open);
    var look = btn.closest('[data-kv-look]');
    var row = look && look.querySelector('[data-kv-look-row-for="' + id + '"]');
    if (row) {
      var rb = row.querySelector('[data-kv-look-row]');
      if (rb) rb.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && lookPhone.matches) {
        var list = row.parentElement;
        try { list.scrollTo({ left: row.offsetLeft - list.offsetLeft - (parseFloat(window.getComputedStyle(list).paddingLeft) || 0), behavior: reduce && reduce.matches ? 'auto' : 'smooth' }); } catch (e) { list.scrollLeft = row.offsetLeft; }
      }
    }
    if (look) lookRows(look);
  }
  function lookRows(look) {
    var rows = qa('[data-kv-look-row-for]', look);
    var any = false;
    rows.forEach(function (r) {
      var b = look.querySelector('[data-kv-hotspot][aria-controls="' + r.getAttribute('data-kv-look-row-for') + '"]');
      var on = !!b && b.getAttribute('aria-expanded') === 'true';
      r.classList.toggle('is-on', on);
      any = any || on;
    });
    if (!any && rows[0]) rows[0].classList.add('is-on');
  }
  function closeSpots(except) {
    qa('[data-kv-hotspot][aria-expanded="true"]').forEach(function (b) { if (b !== except) setSpot(b, false); });
  }
  function toggleSpot(hs) {
    var open = lookPhone.matches || hs.getAttribute('aria-expanded') !== 'true';
    closeSpots(hs);
    setSpot(hs, open);
  }
  document.addEventListener('pointerover', function (e) {
    if (e.pointerType !== 'mouse' || lookPhone.matches || !e.target.closest) return;
    var spot = e.target.closest('.kv-look__spot');
    if (!spot || spot.contains(e.relatedTarget)) return;
    var hs = spot.querySelector('[data-kv-hotspot]');
    // the click that usually follows the hover must not close what the hover just opened
    if (hs && hs.getAttribute('aria-expanded') !== 'true') { closeSpots(hs); setSpot(hs, true); hs.setAttribute('data-kv-hover-open', ''); }
  });
  document.addEventListener('pointerout', function (e) {
    if (e.pointerType !== 'mouse' || lookPhone.matches || !e.target.closest) return;
    var spot = e.target.closest('.kv-look__spot');
    if (!spot || spot.contains(e.relatedTarget)) return;
    var hs = spot.querySelector('[data-kv-hotspot]');
    if (hs) hs.removeAttribute('data-kv-hover-open');
    if (hs && hs.getAttribute('aria-expanded') === 'true') setSpot(hs, false);
  });

  /* ---------- rail ---------- */
  function railStep(track) {
    var item = track.querySelector('.kv-rail__item');
    if (!item) return track.clientWidth;
    var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;

    var add = t.closest('[data-kv-card-add]');
    if (add) {
      e.preventDefault();
      if (add.getAttribute('data-single') === 'true') {
        addItems([add.getAttribute('data-variant-id')], add).then(function (j) {
          // Lets the wishlist page (T39) treat "Move to cart" as a move.
          if (j) add.dispatchEvent(new CustomEvent('kv:added', { bubbles: true }));
        });
      }
      else window.location.href = add.getAttribute('data-url');
      return;
    }
    var ids = t.closest('[data-kv-add-ids]');
    if (ids) {
      e.preventDefault();
      addItems(ids.getAttribute('data-kv-add-ids').split(',').filter(Boolean), ids);
      closeSpots();
      return;
    }
    var sw = t.closest('.kv-pc__sw');
    if (sw) { e.preventDefault(); selectSwatch(sw); return; }

    // the rest of a product card (price, spacing) opens the product page like the image and title links
    var card = t.closest('[data-kv-card][data-url]');
    if (card && !t.closest('a, button, input, select, textarea, label, [role="button"], [data-kv-fav], [data-kv-notify-open]')) {
      var sel = window.getSelection && String(window.getSelection());
      if (!sel) {
        if (e.metaKey || e.ctrlKey) window.open(card.getAttribute('data-url'), '_blank');
        else window.location.href = card.getAttribute('data-url');
      }
      return;
    }

    var quick = t.closest('[data-kv-quick]');
    if (quick && modal()) {
      e.preventDefault();
      closeSpots();
      qvOpen(quick.getAttribute('data-kv-quick'), quick);
      return;
    }

    var bar = t.closest('[data-kv-facets]');
    var sortToggle = t.closest('[data-kv-sort-toggle]');
    if (sortToggle) {
      var list = document.getElementById(sortToggle.getAttribute('aria-controls'));
      if (list) {
        var opening = list.hidden;
        list.hidden = !opening;
        sortToggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) {
          var cur = list.querySelector('.kv-sort__opt.is-on') || list.querySelector('.kv-sort__opt');
          if (cur) cur.focus();
        }
      }
      return;
    }
    var opt = t.closest('[data-kv-sort]');
    if (opt) {
      var form = facetsForm();
      var value = opt.getAttribute('data-kv-sort');
      if (form) {
        var input = form.querySelector('[name="sort_by"]');
        if (input) input.value = value;
        window.location.href = formUrl(form, { sort_by: value });
      }
      return;
    }
    qa('[data-kv-facets]').forEach(function (b) { closeSort(b); });

    var view = t.closest('[data-kv-view]');
    if (view && bar) { setView(bar, view.getAttribute('data-kv-view')); return; }

    var tab = t.closest('[data-kv-tabs] [role="tab"]');
    if (tab) { selectTab(tab); return; }

    var hs = t.closest('[data-kv-hotspot]');
    if (hs) {
      if (hs.hasAttribute('data-kv-hover-open')) { hs.removeAttribute('data-kv-hover-open'); return; }
      toggleSpot(hs);
      return;
    }
    var lookRow = t.closest('[data-kv-look-row]');
    if (lookRow) {
      var rowLook = lookRow.closest('[data-kv-look]');
      var rowSpot = rowLook && rowLook.querySelector('[data-kv-hotspot][aria-controls="' + lookRow.getAttribute('aria-controls') + '"]');
      if (rowSpot) toggleSpot(rowSpot);
      return;
    }
    if (!t.closest('.kv-look__pop')) closeSpots();

    var prev = t.closest('[data-kv-rail-prev]');
    var next = t.closest('[data-kv-rail-next]');
    if (prev || next) {
      var track = (prev || next).closest('[data-kv-rail]').querySelector('[data-kv-rail-track]');
      if (!track) return;
      var dx = railStep(track) * (next ? 1 : -1);
      try { track.scrollBy({ left: dx, behavior: reduce && reduce.matches ? 'auto' : 'smooth' }); }
      catch (err) { track.scrollLeft += dx; }
    }
  });

  /* ---------- keyboard ---------- */
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t.closest) return;
    var tab = t.closest('[data-kv-tabs] [role="tab"]');
    if (tab && /^(ArrowLeft|ArrowRight|Home|End)$/.test(e.key)) {
      var tabs = qa('[role="tab"]', tab.closest('[role="tablist"]'));
      var i = tabs.indexOf(tab);
      if (e.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'ArrowRight') i = (i + 1) % tabs.length;
      else if (e.key === 'Home') i = 0;
      else i = tabs.length - 1;
      e.preventDefault();
      selectTab(tabs[i], true);
      return;
    }
    var sort = t.closest('.kv-sort');
    if (sort) {
      if (e.key === 'Escape') { e.preventDefault(); closeSort(sort.closest('[data-kv-facets]'), true); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var opts = qa('.kv-sort__opt', sort);
        var j = opts.indexOf(t);
        if (j > -1) { e.preventDefault(); opts[(j + (e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length].focus(); }
      }
      return;
    }
    if (e.key === 'Escape') {
      var openSpot = document.querySelector('[data-kv-hotspot][aria-expanded="true"]');
      if (openSpot) { setSpot(openSpot, false); openSpot.focus(); }
    }
  });

  /* ---------- init: remembered grid view ---------- */
  function init() {
    qa('[data-kv-look]').forEach(lookRows);
    // a piece marked "open on load" shows its card on desktop (board: Complete the Look opens the Lindy card)
    if (!lookPhone.matches) qa('[data-kv-hotspot][data-kv-open]').forEach(function (b) { setSpot(b, true); });
    var cols = null;
    try { cols = window.localStorage.getItem('kv-grid-view'); } catch (e) { cols = null; }
    if (cols !== '3') return;
    qa('[data-kv-facets]').forEach(function (bar) { setView(bar, cols); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Product page (T33) - [data-kv-product] in kevlts-main-product.
   Gallery: thumbnails / dots follow the scroll position; mouse drag
   scrolls (a drag never opens the lightbox); arrow keys step panels.
   Options: radio groups -> variant (from the inline JSON with
   pre-formatted prices), price, add button, ?variant= URL, the
   variant's image; unavailable combinations are dimmed. A Size option
   without a variant in the URL starts unselected and Add shows
   "Please choose a size first." Add uses window.kvCart.add (T20
   contract: cart drawer section + cart:update); without it the form
   posts normally. Delivery estimate: business days (Sunday-Thursday)
   from today for the chosen country row. Lightbox: native dialog,
   click to zoom (follows the pointer), arrows, Escape; closing syncs
   the gallery. Back: returns to the previous page on this store.
   Recommendations: [data-kv-recs] loads its section and shows it
   only when there are products.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function addBusinessDays(from, n) {
    var d = new Date(from.getTime());
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      var w = d.getDay();
      if (w !== 5 && w !== 6) added++;
    }
    return d;
  }
  function fmtDate(d) {
    try {
      // British English on the boards ("Wed 21 Oct"), so plain "en" reads day-month
      var lang = document.documentElement.lang || undefined;
      if (lang === 'en') lang = 'en-GB';
      return new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
    } catch (e) { return d.toDateString(); }
  }
  function arrival(min, max, now) {
    var a = min > 0 ? min : 1;
    var b = max >= a ? max : a;
    var start = now || new Date();
    var d1 = addBusinessDays(start, a);
    return a === b ? fmtDate(d1) : fmtDate(d1) + ' – ' + fmtDate(addBusinessDays(start, b));
  }

  function init(root) {
    if (!root || root.dataset.kvReady) return;
    root.dataset.kvReady = '1';

    /* ---------- gallery ---------- */
    var track = root.querySelector('[data-kv-track]');
    var pans = track ? qa('.kv-pb__pan', track) : [];
    var thumbs = qa('.kv-pb__stage > .kv-pb__th [data-kv-go]', root);
    var dots = qa('.kv-pb__dots i', root);
    function step() {
      if (!pans.length) return 1;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return pans[0].getBoundingClientRect().width + gap;
    }
    function current() { return Math.max(0, Math.min(pans.length - 1, Math.round(track.scrollLeft / step()))); }
    function setActive(i) {
      thumbs.forEach(function (t, k) {
        t.classList.toggle('is-on', k === i);
        if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      dots.forEach(function (d, k) { d.classList.toggle('is-on', k === i); });
    }
    function go(i, instant) {
      var p = pans[i];
      if (!p || !track) return;
      var left = p.offsetLeft - track.offsetLeft;
      var smooth = !instant && !(reduce && reduce.matches);
      try { track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' }); } catch (e) { track.scrollLeft = left; }
      setActive(i);
    }
    root.kvGo = go;
    if (track) {
      track.addEventListener('scroll', function () { if (!drag) setActive(current()); }, { passive: true });
      track.addEventListener('keydown', function (e) {
        if (e.target !== track) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          go(Math.max(0, Math.min(pans.length - 1, current() + (e.key === 'ArrowRight' ? 1 : -1))));
        }
      });
      var drag = null;
      var moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag = { x: e.clientX, left: track.scrollLeft };
        moved = false;
      });
      track.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (!moved && Math.abs(dx) > 5) { moved = true; track.classList.add('is-drag'); }
        if (moved) track.scrollLeft = drag.left - dx;
      });
      var endDrag = function () {
        if (!drag) return;
        drag = null;
        if (moved) { track.classList.remove('is-drag'); go(current()); }
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointerleave', endDrag);
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; return; }
        var o = e.target.closest('[data-kv-lb-open]');
        if (o) { e.preventDefault(); lbOpen(Number(o.getAttribute('data-kv-lb-open')), o); }
      }, true);
    }
    thumbs.forEach(function (t, k) { t.addEventListener('click', function () { go(k); }); });

    /* ---------- options / variant ---------- */
    var variants = [];
    try { variants = JSON.parse(root.querySelector('[data-kv-variants]').textContent); } catch (e) { variants = []; }
    var form = root.querySelector('[data-kv-pb-form]');
    var idInput = root.querySelector('[data-kv-variant-id]');
    var addBtn = root.querySelector('[data-kv-pb-add]');
    var priceEl = root.querySelector('[data-kv-price]');
    var groups = qa('[data-kv-opt]', root);

    function selected() {
      return groups.map(function (g) {
        var c = g.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
    function matches(v, sel) {
      return v.options.every(function (o, k) { return sel[k] === null || sel[k] === undefined || o === sel[k]; });
    }
    function findVariant(sel) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].options.every(function (o, k) { return o === sel[k]; })) return variants[i];
      }
      return null;
    }
    function markAvailability(sel) {
      groups.forEach(function (g, gi) {
        qa('input', g).forEach(function (inp) {
          var test = sel.slice();
          test[gi] = inp.value;
          var ok = variants.some(function (v) { return v.available && matches(v, test); });
          var label = inp.closest('label');
          if (label) label.classList.toggle('is-na', !ok);
        });
      });
    }
    var notifyBtn = root.querySelector('[data-kv-notify-open]');
    var soLabel = root.querySelector('[data-kv-so]');
    // soldOut: a real variant that is sold out -> "Sold out" + "Get notified" (G09) when the notify form exists.
    function setAdd(enabled, soldOut) {
      if (!addBtn) return;
      var lab = addBtn.querySelector('[data-kv-add-label]');
      addBtn.disabled = !enabled;
      if (lab) lab.textContent = enabled ? addBtn.getAttribute('data-label-add') : addBtn.getAttribute('data-label-sold');
      var notifying = !!(soldOut && notifyBtn);
      addBtn.hidden = notifying;
      if (notifyBtn) notifyBtn.hidden = !notifying;
      if (soLabel) soLabel.hidden = !soldOut;
    }
    function setNotifyVariant(v) {
      var field = document.querySelector('#kv-notify-' + root.getAttribute('data-section-id') + ' [data-kv-notify-product]');
      var name = document.querySelector('#kv-notify-' + root.getAttribute('data-section-id') + ' [data-kv-notify-variant]');
      if (!field || !v) return;
      field.value = field.getAttribute('data-title') + (name ? ' (' + v.title + ')' : '') + ' - ' + field.getAttribute('data-base') + '?variant=' + v.id;
      if (name) name.textContent = v.title;
    }
    function update(fromUser) {
      var sel = selected();
      groups.forEach(function (g, gi) {
        var b = g.querySelector('[data-kv-opt-val]');
        if (b) b.textContent = sel[gi] || '';
      });
      if (!groups.length) return;
      markAvailability(sel);
      var pending = sel.some(function (s) { return s === null; });
      if (pending) { setAdd(true); return; }
      qa('[data-kv-size-err]', root).forEach(function (er) { er.hidden = true; });
      var v = findVariant(sel);
      if (!v) { setAdd(false); return; }
      if (idInput) idInput.value = v.id;
      if (priceEl) {
        priceEl.textContent = '';
        if (v.compare) { var s = document.createElement('s'); s.textContent = v.compare; priceEl.appendChild(s); }
        var sp = document.createElement('span'); sp.textContent = v.price; priceEl.appendChild(sp);
      }
      setAdd(v.available, !v.available);
      if (!v.available) setNotifyVariant(v);
      if (fromUser) {
        try { window.history.replaceState(window.history.state, '', window.location.pathname + '?variant=' + v.id); } catch (e) { /* ignore */ }
        if (v.media) {
          for (var k = 0; k < pans.length; k++) {
            if (pans[k].getAttribute('data-media-id') === String(v.media)) { go(k); break; }
          }
        }
      }
    }
    groups.forEach(function (g) { g.addEventListener('change', function () { update(true); }); });

    if (form) {
      form.addEventListener('submit', function (e) {
        var sel = selected();
        var missing = -1;
        for (var i = 0; i < sel.length; i++) { if (sel[i] === null) { missing = i; break; } }
        if (missing > -1) {
          e.preventDefault();
          var er = groups[missing].querySelector('[data-kv-size-err]');
          if (er) er.hidden = false;
          var first = groups[missing].querySelector('input');
          if (first) first.focus();
          return;
        }
        if (!window.kvCart || !idInput) return;
        e.preventDefault();
        window.kvCart.add([idInput.value], addBtn);
      });
    }
    update(false);

    /* ---------- delivery estimate ---------- */
    // The purchase card's picker sets the country; the Delivery tab shows it read only. The header's ship-to
    // choice (kv:ship) pre-selects it.
    var dvSelects = qa('[data-kv-dv-select]', root);
    if (dvSelects.length) {
      var est = function (value) {
        dvSelects.forEach(function (sEl) {
          if (value && sEl.value !== value) {
            sEl.value = value;
            sEl.dispatchEvent(new Event('change'));
          }
        });
        var o = dvSelects[0].options[dvSelects[0].selectedIndex];
        if (!o) return;
        var date = o.getAttribute('data-today') || arrival(Number(o.getAttribute('data-min')), Number(o.getAttribute('data-max')));
        var areas = null;
        qa('[data-kv-ar]', root).forEach(function (el) {
          el.hidden = el.getAttribute('data-kv-ar') !== o.value;
          if (!el.hidden) areas = el;
        });
        qa('[data-kv-dv-arr]', root).forEach(function (el) { el.hidden = !!areas; });
        // a delivery area with its own fee sets the delivery fee for that country
        var area = areas && areas.querySelector('[role="option"][aria-selected="true"]');
        var fee = (area && area.getAttribute('data-fee')) || o.getAttribute('data-fee') || '';
        var days = o.getAttribute('data-days') || '';
        var note = o.getAttribute('data-note') || '';
        qa('[data-kv-dv-fee]', root).forEach(function (el) { el.textContent = fee; });
        // the arrival line's "· Delivery <fee>" only for a Shopify market (Qatar); other destinations are estimates
        qa('[data-kv-dv-feeline]', root).forEach(function (el) { el.hidden = !o.hasAttribute('data-market'); });
        var code = (o.getAttribute('data-code') || '').toLowerCase();
        // Delivery fee row of Delivery & returns: data-kv-dv-feerow="qa" (setting delivery_fees_qatar_only, the default)
        // shows the whole row only for Qatar; "all" always shows it.
        qa('[data-kv-dv-feerow]', root).forEach(function (el) { el.hidden = el.getAttribute('data-kv-dv-feerow') === 'qa' && code !== 'qa'; });
        qa('[data-kv-dv-name]', root).forEach(function (el) { el.textContent = o.textContent.trim(); });
        qa('[data-kv-dv-flag]', root).forEach(function (el) {
          el.hidden = !code;
          if (code) el.setAttribute('src', '//cdn.shopify.com/static/images/flags/' + code + '.svg?width=40');
        });
        qa('[data-kv-dv-date]', root).forEach(function (el) { el.textContent = date; });
        qa('[data-kv-dv-days]', root).forEach(function (el) { el.textContent = days; });
        qa('[data-kv-dv-line]', root).forEach(function (el) { el.textContent = note ? days + ' · ' + note : days; });
      };
      dvSelects.forEach(function (sEl) {
        sEl.addEventListener('change', function () { est(sEl.value); });
      });
      var byCode = function (code) {
        var hit = null;
        if (code) qa('option', dvSelects[0]).forEach(function (o) { if (!hit && o.getAttribute('data-code') === code) hit = o.value; });
        return hit;
      };
      var stored = null;
      try { stored = window.localStorage.getItem('kv:ship'); } catch (err) { /* storage blocked */ }
      est(byCode(stored));
      document.addEventListener('kv:ship', function (e) { est(byCode(e.detail && e.detail.code)); });
      qa('[data-kv-dvb-tip]', root).forEach(function (b) {
        b.addEventListener('click', function () {
          var tip = document.getElementById(b.getAttribute('aria-controls'));
          if (!tip) return;
          tip.hidden = !tip.hidden;
          b.setAttribute('aria-expanded', tip.hidden ? 'false' : 'true');
        });
      });
    }

    /* ---------- reviews summary: share per star from the loaded Judge.me widget when the page could not tell ---------- */
    var rvRows = root.querySelector('[data-kv-rvp-rows][hidden]');
    if (rvRows) {
      var rvFill = function () {
        var hist = qa('#judgeme_product_reviews [data-rating][data-frequency]');
        if (!hist.length) return false;
        var total = 0;
        var f = {};
        hist.forEach(function (h) { var n = Number(h.getAttribute('data-frequency')) || 0; f[h.getAttribute('data-rating')] = n; total += n; });
        if (!total) return false;
        qa('[data-kv-rvp-star]', rvRows).forEach(function (r) {
          var pct = Math.round(((f[r.getAttribute('data-kv-rvp-star')] || 0) * 100) / total);
          r.querySelector('b').style.width = pct + '%';
          r.querySelector('[data-kv-rvp-pct]').textContent = pct + '%';
          r.classList.toggle('is-z', pct === 0);
        });
        rvRows.hidden = false;
        return true;
      };
      if (!rvFill() && 'MutationObserver' in window) {
        var rvMo = new MutationObserver(function () { if (rvFill()) rvMo.disconnect(); });
        rvMo.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () { rvMo.disconnect(); }, 15000);
      }
    }

    /* ---------- back ---------- */
    var back = root.querySelector('[data-kv-back]');
    if (back) {
      back.addEventListener('click', function (e) {
        try {
          if (document.referrer && new URL(document.referrer).origin === window.location.origin && window.history.length > 1) {
            e.preventDefault();
            window.history.back();
          }
        } catch (err) { /* follow the link */ }
      });
    }

    /* ---------- lightbox ---------- */
    var lb = root.querySelector('[data-kv-lb]');
    var lbImg = lb && lb.querySelector('[data-kv-lb-img]');
    var fulls = track ? qa('[data-kv-lb-open]', track).map(function (b) { return b.getAttribute('data-full'); }) : [];
    var lbIdx = 0;
    var lbOpener = null;
    function lbShow(i) {
      if (!fulls.length) return;
      lbIdx = (i + fulls.length) % fulls.length;
      lbImg.classList.remove('is-z');
      lbImg.style.transformOrigin = '';
      lbImg.setAttribute('src', fulls[lbIdx]);
      qa('[data-kv-lb-go]', lb).forEach(function (b, k) { b.classList.toggle('is-on', k === lbIdx); });
    }
    function lbCleanup() {
      if (!lb || !lb.kvOpen) return;
      lb.kvOpen = false;
      document.documentElement.classList.remove('kv-lb-open');
      go(lbIdx, true);
      if (lbOpener && document.contains(lbOpener)) lbOpener.focus();
      lbOpener = null;
    }
    function lbClose() {
      if (!lb) return;
      if (lb.open) { if (typeof lb.close === 'function') lb.close(); else lb.removeAttribute('open'); }
      lbCleanup();
    }
    function lbOpen(i, opener) {
      if (!lb || !lbImg) return;
      lbOpener = opener || document.activeElement;
      lbShow(i);
      lb.kvOpen = true;
      if (!lb.open) { if (typeof lb.showModal === 'function') lb.showModal(); else lb.setAttribute('open', ''); }
      document.documentElement.classList.add('kv-lb-open');
      var x = lb.querySelector('[data-kv-lb-close]');
      if (x) x.focus();
    }
    function origin(e) {
      var r = lbImg.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      var y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      lbImg.style.transformOrigin = x + '% ' + y + '%';
    }
    if (lb) {
      lb.addEventListener('click', function (e) {
        if (e.target.closest('[data-kv-lb-close]')) { lbClose(); return; }
        var g = e.target.closest('[data-kv-lb-go]');
        if (g) { lbShow(Number(g.getAttribute('data-kv-lb-go'))); return; }
        if (e.target === lbImg) {
          if (lbImg.classList.contains('is-z')) lbImg.classList.remove('is-z');
          else { origin(e); lbImg.classList.add('is-z'); }
        }
      });
      lb.addEventListener('pointermove', function (e) { if (lbImg.classList.contains('is-z')) origin(e); });
      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); lbShow(lbIdx + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); lbShow(lbIdx - 1); }
      });
      lb.addEventListener('cancel', function (e) { e.preventDefault(); lbClose(); });
      // Ignore a stale 'close' event that arrives after the lightbox was reopened.
      lb.addEventListener('close', function () { if (!lb.open) lbCleanup(); });
    }
    root.kvLightbox = { open: lbOpen, close: lbClose };
  }

  function loadRecs(el) {
    if (!el || el.dataset.kvReady) return;
    el.dataset.kvReady = '1';
    var url = el.getAttribute('data-url');
    if (!url || (!el.hidden && el.children.length)) return;
    fetch(url)
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var n = doc.querySelector('[data-kv-recs]');
        if (n && n.children.length) { el.innerHTML = n.innerHTML; el.hidden = false; }
      })
      .catch(function () { /* stays hidden */ });
  }

  function scan() {
    qa('[data-kv-product]').forEach(init);
    qa('[data-kv-recs]').forEach(loadRecs);
  }
  window.kvProduct = { arrival: arrival, addBusinessDays: addBusinessDays };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  document.addEventListener('shopify:section:load', scan);
})();

/* ============================================================
   Accounts, sign in, addresses, wishlist (T37-T39).
   - [data-kv-auth-form]: checks email (and password when present,
     terms when present) before Shopify's form posts; focuses
     Shopify's own error summary; password show / hide.
   - [data-kv-account]: #overview / #orders / #details panels
     (?page= opens Orders); without JS every panel stays visible.
   - Address forms: country -> province list from Shopify's
     all_country_option_tags data-provinces; remove asks first.
   - Wishlist: the theme's local wishlist (browser storage key
     theme4:wishlist:id, event theme4:wishlist:update; the theme's
     custom.js still opens /search?view=wishlist with the ids).
     [data-kv-fav] hearts toggle (aria-pressed), [data-kv-fav-remove]
     and "Move to cart" remove a card on the wishlist page,
     [data-kv-fav-count] shows the count. window.kvWishlist exposes
     ids / has / add / remove for tests and other sections.
   ============================================================ */
(function () {
  'use strict';

  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------- sign in / create account ---------- */
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function fieldError(input, message) {
    var field = input.closest('.kv-field') || input.parentNode;
    var id = input.id + '-kv-error';
    var el = document.getElementById(id);
    if (!message) {
      if (el) el.remove();
      input.removeAttribute('aria-invalid');
      var d = (input.getAttribute('aria-describedby') || '').replace(id, '').trim();
      if (d) input.setAttribute('aria-describedby', d); else input.removeAttribute('aria-describedby');
      return true;
    }
    if (!el) {
      el = document.createElement('p');
      el.className = 'kv-field__error';
      el.id = id;
      el.setAttribute('role', 'alert');
      field.appendChild(el);
    }
    el.textContent = message;
    input.setAttribute('aria-invalid', 'true');
    var desc = input.getAttribute('aria-describedby') || '';
    if (desc.indexOf(id) < 0) input.setAttribute('aria-describedby', (desc + ' ' + id).trim());
    return false;
  }
  // Messages come from the carrier in the form's own section; a missing message still counts as an error.
  function msg(key, form) {
    var scope = form && form.closest('section');
    var m = (scope && scope.querySelector('[data-kv-auth-msgs]')) || document.querySelector('[data-kv-auth-msgs]');
    return (m && m.getAttribute('data-' + key)) || '!';
  }
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form.matches && form.matches('[data-kv-confirm]')) {
      if (!window.confirm(form.getAttribute('data-kv-confirm'))) e.preventDefault();
      return;
    }
    if (!form.matches || !form.matches('[data-kv-auth-form]')) return;
    if (e.submitter && e.submitter.hasAttribute('data-kv-skip-checks')) return;
    var firstBad = null;
    var email = form.querySelector('input[type="email"]');
    if (email && !fieldError(email, EMAIL.test(email.value.trim()) ? '' : msg('email', form))) firstBad = firstBad || email;
    qa('input[type="password"][required], [data-kv-pw] input[required]', form).forEach(function (pw) {
      if (!fieldError(pw, pw.value ? '' : msg('password', form))) firstBad = firstBad || pw;
    });
    qa('[data-kv-match]', form).forEach(function (wrap) {
      var confirm = wrap.querySelector('input');
      var original = document.getElementById(wrap.getAttribute('data-kv-match'));
      if (confirm && original && confirm.value && original.value && confirm.value !== original.value) {
        if (!fieldError(confirm, msg('mismatch', form))) firstBad = firstBad || confirm;
      }
    });
    var terms = form.querySelector('[data-kv-terms]');
    var termsErr = form.querySelector('[data-kv-terms-error]');
    if (terms && termsErr) {
      termsErr.hidden = terms.checked;
      if (!terms.checked) firstBad = firstBad || terms;
    }
    if (firstBad) { e.preventDefault(); firstBad.focus(); }
  });
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t.matches && t.matches('[data-kv-terms]')) {
      var er = t.closest('form').querySelector('[data-kv-terms-error]');
      if (er && t.checked) er.hidden = true;
    }
  });
  document.addEventListener('click', function (e) {
    var tg = e.target.closest && e.target.closest('[data-kv-pw-toggle]');
    if (!tg) return;
    var input = tg.closest('[data-kv-pw]').querySelector('input');
    var show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    tg.setAttribute('aria-pressed', show ? 'true' : 'false');
    tg.textContent = show ? tg.getAttribute('data-hide') : tg.getAttribute('data-show');
  });

  /* ---------- account panels ---------- */
  function showPanel(root, name, push) {
    var panels = qa('[data-kv-ac-panel]', root);
    if (!panels.some(function (p) { return p.getAttribute('data-kv-ac-panel') === name; })) name = 'overview';
    panels.forEach(function (p) { p.hidden = p.getAttribute('data-kv-ac-panel') !== name; });
    qa('.kv-ac__nav [data-kv-ac-tab]', root).forEach(function (t) {
      if (t.getAttribute('data-kv-ac-tab') === name) t.setAttribute('aria-current', 'page');
      else t.removeAttribute('aria-current');
    });
    if (push) { try { window.history.replaceState(window.history.state, '', '#' + name); } catch (e) { /* ignore */ } }
  }
  function initAccount(root) {
    if (!root || root.dataset.kvReady) return;
    root.dataset.kvReady = '1';
    var name = (window.location.hash || '').replace('#', '');
    if (!name && /[?&]page=/.test(window.location.search)) name = 'orders';
    showPanel(root, name || 'overview', false);
    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-kv-ac-tab]');
      if (!t) return;
      e.preventDefault();
      showPanel(root, t.getAttribute('data-kv-ac-tab'), true);
      var panel = root.querySelector('[data-kv-ac-panel="' + t.getAttribute('data-kv-ac-tab') + '"]');
      var h = panel && panel.querySelector('h2');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
    });
  }

  /* ---------- addresses ---------- */
  function fillProvinces(country) {
    var province = document.getElementById(country.getAttribute('data-province'));
    if (!province) return;
    var opt = country.options[country.selectedIndex];
    var list = [];
    try { list = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]'); } catch (e) { list = []; }
    var current = province.value || province.getAttribute('data-default') || '';
    province.textContent = '';
    list.forEach(function (p) {
      var o = document.createElement('option');
      o.value = p[0];
      o.textContent = p[1];
      if (p[0] === current || p[1] === current) o.selected = true;
      province.appendChild(o);
    });
    var wrap = province.closest('[data-kv-province-wrap]');
    if (wrap) wrap.hidden = list.length === 0;
  }
  function initAddresses() {
    qa('[data-kv-country]').forEach(function (c) {
      if (c.dataset.kvReady) return;
      c.dataset.kvReady = '1';
      var def = c.getAttribute('data-default');
      if (def) {
        for (var i = 0; i < c.options.length; i++) {
          if (c.options[i].value === def || c.options[i].textContent === def) { c.selectedIndex = i; break; }
        }
      }
      fillProvinces(c);
      c.addEventListener('change', function () {
        var province = document.getElementById(c.getAttribute('data-province'));
        if (province) province.setAttribute('data-default', '');
        fillProvinces(c);
      });
    });
  }

  /* ---------- wishlist (theme storage) ---------- */
  var KEY = 'theme4:wishlist:id';
  var LIMIT = 50;
  function read() {
    try {
      var v = window.localStorage.getItem(KEY);
      return v ? v.split(',').filter(Boolean) : [];
    } catch (e) { return []; }
  }
  function write(ids) {
    try { window.localStorage.setItem(KEY, ids.join(',')); } catch (e) { /* storage blocked: UI still updates */ }
    document.dispatchEvent(new CustomEvent('theme4:wishlist:update', { bubbles: true, detail: 'kevlts' }));
    // every save / remove / move to cart updates the header count and links at once
    syncBadges(ids);
  }
  function has(id) { return read().indexOf(String(id)) > -1; }
  function add(id) {
    id = String(id);
    var ids = read().filter(function (x) { return x !== id; });
    ids.unshift(id);
    if (ids.length > LIMIT) ids = ids.slice(0, LIMIT);
    write(ids);
  }
  function remove(id) {
    id = String(id);
    write(read().filter(function (x) { return x !== id; }));
  }
  function syncHearts() {
    var ids = read();
    qa('[data-kv-fav]').forEach(function (b) {
      var on = ids.indexOf(b.getAttribute('data-kv-fav')) > -1;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = b.getAttribute(on ? 'data-label-on' : 'data-label-off');
      if (label) { b.setAttribute('aria-label', label); b.setAttribute('title', label); }
    });
    qa('[data-kv-fav-count]').forEach(function (c) { c.textContent = String(ids.length); });
    syncBadges(ids);
  }
  // Header wishlist count (Halo Pop): hidden at 0, 99+ from 100; it pops on every change, and the heart beats when a
  // piece is added. The link's accessible name carries the count ("Wishlist, 3 items"). Every wishlist link opens the
  // current saved pieces directly (the theme's search URL format), so a removed piece never comes back.
  function syncBadges(ids) {
    var n = ids.length;
    qa('[data-kv-wl-link]').forEach(function (a) {
      var base = a.getAttribute('data-kv-wl-link');
      a.setAttribute('href', n ? base + '&type=product&options[unavailable_products]=last&q=' + encodeURI('id:' + ids.join(' OR id:')) : base);
    });
    qa('[data-kv-fav-badge]').forEach(function (b) {
      var prev = b.hasAttribute('data-n') ? Number(b.getAttribute('data-n')) : -1;
      b.setAttribute('data-n', String(n));
      b.textContent = n > 99 ? '99+' : String(n);
      b.hidden = n === 0;
      var link = b.closest('a');
      if (link && link.getAttribute('data-label')) {
        var tpl = link.getAttribute(n === 1 ? 'data-count-one' : 'data-count-other') || '';
        link.setAttribute('aria-label', link.getAttribute('data-label') + (n ? ', ' + tpl.replace(/\d+/, String(n)) : ''));
      }
      if (prev < 0 || n === prev) return;
      var heart = n > prev ? b.parentNode.querySelector('.kv-icon') : null;
      [b, heart].forEach(function (el) {
        if (!el) return;
        el.classList.remove('is-pop');
        void el.getBoundingClientRect();
        el.classList.add('is-pop');
      });
    });
  }
  // On the wishlist page: saved ids the store no longer returns (deleted, unpublished, hidden) are dropped, so the
  // header count always equals the pieces listed. Only ids this page asked for (search.terms) can be dropped.
  function pruneMissing() {
    var page = document.querySelector('[data-kv-wishlist][data-kv-wl-terms]');
    if (!page) return;
    var asked = page.getAttribute('data-kv-wl-terms').match(/\d{5,}/g) || [];
    if (!asked.length) return;
    var shown = qa('[data-kv-wl-grid] [data-kv-card]', page).map(function (c) { return c.getAttribute('data-product-id'); });
    var missing = asked.filter(function (id) { return shown.indexOf(id) === -1; });
    if (!missing.length) return;
    write(read().filter(function (id) { return missing.indexOf(id) === -1; }));
  }
  function updatePageCount() {
    var page = document.querySelector('[data-kv-wishlist]');
    if (!page) return;
    var n = qa('[data-kv-wl-grid] [data-kv-card]', page).length;
    var c = page.querySelector('[data-kv-wl-count]');
    if (c) c.textContent = (c.getAttribute(n === 1 ? 'data-one' : 'data-other') || '[n]').replace('[n]', n);
    var empty = page.querySelector('[data-kv-wl-empty]');
    var grid = page.querySelector('[data-kv-wl-grid]');
    if (empty) empty.hidden = n > 0;
    if (grid) grid.hidden = n === 0;
  }
  function removeCard(card) {
    if (!card) return;
    var id = card.getAttribute('data-product-id');
    var next = card.nextElementSibling || card.previousElementSibling;
    if (id) remove(id);
    card.remove();
    updatePageCount();
    var target = next && (next.querySelector('[data-kv-fav-remove]') || next.querySelector('a'));
    if (!target) target = document.querySelector('[data-kv-wl-empty] a');
    if (target) target.focus();
  }
  document.addEventListener('click', function (e) {
    var heart = e.target.closest && e.target.closest('[data-kv-fav]');
    if (heart) {
      e.preventDefault();
      var id = heart.getAttribute('data-kv-fav');
      if (has(id)) remove(id); else add(id);
      syncHearts();
      return;
    }
    var rm = e.target.closest && e.target.closest('[data-kv-fav-remove]');
    if (rm) {
      e.preventDefault();
      removeCard(rm.closest('[data-kv-card]'));
    }
  });
  document.addEventListener('kv:added', function (e) {
    var card = e.target.closest && e.target.closest('[data-kv-wishlist] [data-kv-card]');
    if (card) removeCard(card);
  });
  document.addEventListener('theme4:wishlist:update', function (e) { if (e.detail !== 'kevlts') syncHearts(); });
  window.addEventListener('storage', function (e) { if (e.key === KEY) syncHearts(); });
  window.kvWishlist = { ids: read, has: has, add: function (id) { add(id); syncHearts(); }, remove: function (id) { remove(id); syncHearts(); } };

  function init() {
    qa('[data-kv-account]').forEach(initAccount);
    initAddresses();
    pruneMissing();
    syncHearts();
    updatePageCount();
    var err = document.querySelector('[data-kv-form-errors]');
    if (err) err.focus();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Batch 5 (G07-G09).
   - G07 "View" cursor: [data-kv-cursor] follows a mouse pointer over
     product images in product grids (.kv-grid .kv-pc__media): "View",
     "Add" over the add button (native cursor hidden there, as on the
     board). Only for hover-capable fine pointers; never under reduced
     motion; touch and keyboard are unaffected; aria-hidden.
   - G08 delivery country listbox: enhances the product page's native
     select (kept as the source of truth / no-JS fallback) with a flag
     button + listbox (Enter / Space / arrows / Home / End / Escape).
   - G09 notify drawer: "Notify me" is enabled once the email is valid
     (and the privacy box ticked when present); the drawer opens at
     #notify and again after Shopify confirms the contact form.
   ============================================================ */
(function () {
  'use strict';

  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- G07 cursor ---------- */
  // The circle sits exactly on the pointer: its position is written once per frame (no easing, so it never
  // trails the mouse), and its label / colour only change when the target kind changes.
  var cursor = null;
  var cursorKey = '';
  var cursorX = 0;
  var cursorY = 0;
  var cursorFrame = 0;
  function cursorOn() { return !!cursor && !!fine && fine.matches && !(reduce && reduce.matches); }
  function placeCursor() {
    cursorFrame = 0;
    if (cursor) cursor.style.transform = 'translate3d(' + cursorX + 'px,' + cursorY + 'px,0)';
  }
  function hideCursor() {
    if (!cursor || cursor.hidden) return;
    cursor.hidden = true;
    document.documentElement.classList.remove('kv-has-cursor');
  }
  document.addEventListener('pointermove', function (e) {
    if (e.pointerType !== 'mouse' || !cursorOn()) { hideCursor(); return; }
    var t = e.target && e.target.closest ? e.target : null;
    var media = t && t.closest('.kv-grid [data-kv-card] .kv-pc__media');
    var rail = !media && t && t.closest('.kv-rail');
    if (!media && !rail) { hideCursor(); return; }
    var onAdd = media && t.closest('.kv-pc__add');
    var onNotify = media && t.closest('[data-kv-notify-open]');
    var key = rail ? 'drag' : (onAdd ? 'add' : (onNotify ? 'notify' : 'view'));
    if (key !== cursorKey) {
      cursorKey = key;
      cursor.textContent = cursor.getAttribute('data-' + key) || '';
      cursor.classList.toggle('kv-cursor--add', key === 'add');
      cursor.classList.toggle('kv-cursor--light', key === 'drag');
    }
    cursorX = e.clientX;
    cursorY = e.clientY;
    if (cursor.hidden) {
      placeCursor();
      cursor.hidden = false;
      document.documentElement.classList.add('kv-has-cursor');
    } else if (!cursorFrame) {
      cursorFrame = requestAnimationFrame(placeCursor);
    }
  }, { passive: true });
  document.addEventListener('pointerleave', hideCursor);
  window.addEventListener('blur', hideCursor);
  document.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') hideCursor(); });
  if (reduce && reduce.addEventListener) reduce.addEventListener('change', hideCursor);

  /* ---------- Visual QA: lookbook rail drag + phone swipe indicator (Home-Split .rail.grab, mobile .swp) ---------- */
  var drag = null;
  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    var rail = e.target.closest && e.target.closest('.kv-rail');
    if (!rail) return;
    drag = { rail: rail, x: e.clientX, left: rail.scrollLeft, moved: false };
  });
  document.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 5) return;
    if (!drag.moved) { drag.moved = true; drag.rail.classList.add('is-dragging'); }
    drag.rail.scrollLeft = drag.left - dx;
  });
  function endDrag() {
    if (!drag) return;
    var d = drag;
    drag = null;
    d.rail.classList.remove('is-dragging');
    if (d.moved) {
      // a drag is not a click on the look underneath
      var stop = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      d.rail.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(function () { d.rail.removeEventListener('click', stop, { capture: true }); }, 0);
    }
  }
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  document.addEventListener('dragstart', function (e) { if (e.target.closest && e.target.closest('.kv-rail')) e.preventDefault(); });

  /* "New in" view switch (board .vw): standard grid or larger cards (2-up desktop, 1-up phone) */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-kv-gv]');
    if (!b) return;
    var bar = b.closest('[data-kv-hb]');
    var grid = bar && bar.parentNode.querySelector('.kv-grid');
    if (!grid) return;
    var lg = b.getAttribute('data-kv-gv') === 'lg';
    grid.classList.toggle('kv-grid--lg', lg);
    bar.querySelectorAll('[data-kv-gv]').forEach(function (x) {
      var on = x === b;
      x.classList.toggle('is-on', on);
      x.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  });

  /* "Trending now" sort menu (board .tsrt): Shopify sorts the collection (Section Rendering API on the
     collection URL, section kevlts-grid-sort) and the first cards replace the grid */
  function gsClose(gs, focus) {
    var btn = gs.querySelector('[data-kv-gs-toggle]');
    var list = gs.querySelector('[role="listbox"]');
    if (!list || list.hidden) return;
    list.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (focus) btn.focus();
  }
  function gsPick(opt) {
    var gs = opt.closest('[data-kv-gs]');
    var sec = gs.closest('.kv-sec');
    var grid = sec && sec.querySelector(':scope > .kv-grid');
    var value = opt.getAttribute('data-kv-gs-sort');
    qa('[data-kv-gs-sort]', gs).forEach(function (o) {
      var on = o === opt;
      o.classList.toggle('is-on', on);
      o.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    gs.querySelector('[data-kv-gs-label]').textContent = opt.textContent;
    gsClose(gs, true);
    if (!grid) return;
    var limit = parseInt(gs.getAttribute('data-limit'), 10) || 4;
    var url = gs.getAttribute('data-url') + '?section_id=kevlts-grid-sort' + (value ? '&sort_by=' + encodeURIComponent(value) : '');
    grid.classList.add('is-loading');
    grid.setAttribute('aria-busy', 'true');
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var cards = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-kv-gs-cards] > *');
        if (!cards.length) return;
        grid.innerHTML = '';
        for (var i = 0; i < cards.length && i < limit; i++) grid.appendChild(document.importNode(cards[i], true));
        document.dispatchEvent(new CustomEvent('theme4:wishlist:update', { detail: 'kv-gs' }));
      })
      .catch(function () { /* keeps the current cards */ })
      .then(function () { grid.classList.remove('is-loading'); grid.removeAttribute('aria-busy'); });
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    qa('[data-kv-gs]').forEach(function (gs) { if (!gs.contains(t)) gsClose(gs); });
    var tog = t.closest('[data-kv-gs-toggle]');
    if (tog) {
      var gs = tog.closest('[data-kv-gs]');
      var list = gs.querySelector('[role="listbox"]');
      var open = list.hidden;
      list.hidden = !open;
      tog.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) (list.querySelector('.is-on') || list.firstElementChild).focus();
      return;
    }
    var opt = t.closest('[data-kv-gs-sort]');
    if (opt) gsPick(opt);
  });
  document.addEventListener('keydown', function (e) {
    var gs = e.target.closest && e.target.closest('[data-kv-gs]');
    if (!gs) return;
    if (e.key === 'Escape') { gsClose(gs, true); return; }
    var opt = e.target.closest('[data-kv-gs-sort]');
    if (opt && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      var next = e.key === 'ArrowDown' ? opt.nextElementSibling : opt.previousElementSibling;
      if (next) next.focus();
    }
  });

  function updateSwipe(rail) {
    var sec = rail.closest('[data-kv-rail]');
    var swp = sec && sec.querySelector('[data-kv-swp]');
    if (!swp) return;
    var n = parseInt(swp.getAttribute('data-n'), 10) || rail.children.length || 1;
    var max = rail.scrollWidth - rail.clientWidth;
    var p = max > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / max)) : 0;
    var w = 100 / n;
    var pad = function (v) { return (v < 10 ? '0' : '') + v; };
    swp.querySelector('[data-kv-swp-n]').textContent = pad(Math.min(n, Math.round(p * (n - 1)) + 1)) + ' / ' + pad(n);
    var t = swp.querySelector('[data-kv-swp-t]');
    t.style.width = w.toFixed(1) + '%';
    t.style.left = (p * (100 - w)).toFixed(1) + '%';
  }
  document.addEventListener('scroll', function (e) {
    var el = e.target;
    if (el && el.classList && el.classList.contains('kv-rail')) updateSwipe(el);
  }, { capture: true, passive: true });

  /* ---------- G08 delivery country listbox ---------- */
  // Lists in the product card float over the page, since the gallery stage clips the card: above or below the
  // button, wherever they fit under the header (scrolling when neither side has room).
  function floatList(btn, list) {
    if (!btn.closest('.kv-pb__card')) return;
    var r = btn.getBoundingClientRect();
    var hdr = document.querySelector('.kv-header-group');
    var top = Math.max(8, (hdr ? hdr.getBoundingClientRect().bottom : 0) + 8);
    list.style.position = 'fixed';
    list.style.maxHeight = '';
    var h = list.offsetHeight;
    var above = r.top - 8 - top;
    var below = window.innerHeight - r.bottom - 16;
    var fitsUp = h <= above;
    var fitsDown = h <= below;
    var up = list.getAttribute('data-kv-prefer') === 'down'
      ? !fitsDown && (fitsUp || above > below)
      : fitsUp || (!fitsDown && above >= below);
    var room = up ? above : below;
    list.style.left = Math.max(8, Math.min(r.left, window.innerWidth - list.offsetWidth - 8)) + 'px';
    list.style.top = up ? 'auto' : (r.bottom + 8) + 'px';
    list.style.bottom = up ? (window.innerHeight - r.top + 8) + 'px' : 'auto';
    if (h > room) { list.style.maxHeight = room + 'px'; list.style.overflowY = 'auto'; }
  }
  function unfloat(list) {
    ['position', 'left', 'top', 'bottom', 'maxHeight', 'overflowY'].forEach(function (k) { list.style[k] = ''; });
  }

  // Shared listbox behaviour (button + role=listbox list): open on the selected option, arrows / Home / End,
  // Enter or Space to choose, Escape to close. Outside clicks close it; a floating list follows its button on
  // scroll and resize, and closes once the button leaves the screen.
  function listbox(wrap, btn, list, choose) {
    var opts = qa('[role="option"]', list);
    function onAway(e) {
      if (e.type === 'scroll' && list.contains(e.target)) return;
      var r = btn.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) close(false);
      else floatList(btn, list);
    }
    function open() {
      list.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      floatList(btn, list);
      window.addEventListener('scroll', onAway, { capture: true, passive: true });
      window.addEventListener('resize', onAway);
      var cur = list.querySelector('[aria-selected="true"]') || opts[0];
      if (cur) cur.focus({ preventScroll: true });
    }
    function close(focus) {
      if (list.hidden) return;
      list.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      unfloat(list);
      window.removeEventListener('scroll', onAway, { capture: true });
      window.removeEventListener('resize', onAway);
      if (focus) btn.focus();
    }
    function pick(o) {
      choose(o);
      close(true);
    }
    btn.addEventListener('click', function () { if (list.hidden) open(); else close(true); });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); }
    });
    list.addEventListener('click', function (e) {
      var o = e.target.closest('[role="option"]');
      if (o) pick(o);
    });
    list.addEventListener('keydown', function (e) {
      var i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); opts[Math.min(opts.length - 1, i + 1)].focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); opts[Math.max(0, i - 1)].focus(); }
      else if (e.key === 'Home') { e.preventDefault(); opts[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1].focus(); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (i > -1) pick(opts[i]); }
      else if (e.key === 'Escape' || e.key === 'Tab') { if (e.key === 'Escape') e.preventDefault(); close(e.key === 'Escape'); }
    });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(false); });
  }

  function initPicker(dd) {
    if (dd.dataset.kvReady) return;
    var select = dd.querySelector('[data-kv-dv-select]');
    var btn = dd.querySelector('[data-kv-dvp-btn]');
    var list = dd.querySelector('[data-kv-dvp-list]');
    if (!select || !btn || !list) return;
    dd.dataset.kvReady = '1';
    var opts = qa('[role="option"]', list);
    function sync() {
      opts.forEach(function (o) {
        var on = o.getAttribute('data-value') === select.value;
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) {
          var cur = btn.querySelector('[data-kv-dvp-cur]');
          cur.textContent = '';
          var flag = o.querySelector('img');
          if (flag) cur.appendChild(flag.cloneNode(true));
          var n = document.createElement('span');
          n.textContent = o.querySelector('.kv-dvp__n').textContent;
          cur.appendChild(n);
          btn.setAttribute('aria-activedescendant', o.id);
        }
      });
    }
    btn.hidden = false;
    dd.classList.add('is-enhanced');
    sync();
    listbox(dd, btn, list, function (o) {
      select.value = o.getAttribute('data-value');
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
    });
    select.addEventListener('change', sync);
  }

  // Delivery area (e.g. Qatar's municipalities), each with its own fee: the choice is kept for the next product
  // (kv:area), and the product's delivery estimate is re-run so every delivery fee shows the area's fee.
  function initArea(btn) {
    if (btn.dataset.kvReady) return;
    var list = document.getElementById(btn.getAttribute('aria-controls'));
    if (!list) return;
    btn.dataset.kvReady = '1';
    var cur = btn.querySelector('[data-kv-ar-cur]');
    var box = btn.closest('[data-kv-ar]');
    var feeEl = box && box.querySelector('[data-kv-ar-fee]');
    var name = function (o) { var n = o.querySelector('.kv-dvp__n'); return (n || o).textContent.trim(); };
    var set = function (o) {
      qa('[role="option"]', list).forEach(function (x) { x.setAttribute('aria-selected', x === o ? 'true' : 'false'); });
      cur.textContent = name(o);
      btn.setAttribute('aria-activedescendant', o.id);
      var fee = o.getAttribute('data-fee');
      if (feeEl && fee) feeEl.textContent = (feeEl.getAttribute('data-tpl') || '[fee]').replace('[fee]', fee);
      var root = btn.closest('.kv-pb');
      var sel = root && root.querySelector('[data-kv-dv-select]');
      if (sel) sel.dispatchEvent(new Event('change'));
    };
    var stored = null;
    try { stored = window.localStorage.getItem('kv:area'); } catch (err) { /* storage blocked */ }
    qa('[role="option"]', list).forEach(function (o) { if (stored && name(o) === stored) set(o); });
    listbox(btn.parentElement, btn, list, function (o) {
      set(o);
      try { window.localStorage.setItem('kv:area', name(o)); } catch (err) { /* storage blocked */ }
    });
  }

  /* ---------- G09 notify form ---------- */
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function notifyValid(form) {
    var email = form.querySelector('input[type="email"]');
    var agree = form.querySelector('[data-kv-notify-agree]');
    return !!email && EMAIL.test(email.value.trim()) && (!agree || agree.checked);
  }
  function refreshNotify(form) {
    var btn = form.querySelector('[data-kv-notify-submit]');
    if (btn) btn.setAttribute('aria-disabled', notifyValid(form) ? 'false' : 'true');
  }
  document.addEventListener('input', function (e) {
    var f = e.target.closest && e.target.closest('[data-kv-notify-form]');
    if (f) refreshNotify(f);
  });
  document.addEventListener('change', function (e) {
    var f = e.target.closest && e.target.closest('[data-kv-notify-form]');
    if (f) refreshNotify(f);
  });
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f.matches || !f.matches('[data-kv-notify-form]')) return;
    if (!notifyValid(f)) {
      e.preventDefault();
      var email = f.querySelector('input[type="email"]');
      var agree = f.querySelector('[data-kv-notify-agree]');
      if (email && !EMAIL.test(email.value.trim())) { email.setAttribute('aria-invalid', 'true'); email.focus(); }
      else if (agree && !agree.checked) agree.focus();
    }
  });

  function init() {
    cursor = document.querySelector('[data-kv-cursor]');
    qa('.kv-pb__dv-dd').forEach(initPicker);
    qa('[data-kv-ar-btn]').forEach(initArea);
    qa('[data-kv-notify-form]').forEach(refreshNotify);
    var notify = document.querySelector('[data-kv-notify]');
    if (notify && window.kvDrawer) {
      var trigger = document.querySelector('[data-kv-notify-open]');
      if (notify.querySelector('[data-kv-notify-sent], [data-kv-form-errors]')) window.kvDrawer.open(notify.id, trigger);
      else if (window.location.hash === '#notify' && trigger && !trigger.hidden) window.kvDrawer.open(notify.id, trigger);
    }
  }
  window.kvPicker = { init: initPicker };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Shop by Category (new edits 10/26, board .sbc): the tile under
   the pointer or focus becomes the active one (is-on) and stays
   active until another tile is reached; the "01 / 04" counter
   shows its number.
   ============================================================ */
(function () {
  function activate(tile) {
    var sec = tile.closest('[data-kv-sbc]');
    if (!sec || tile.classList.contains('is-on')) return;
    sec.querySelectorAll('[data-kv-sbc-tile]').forEach(function (t) { t.classList.toggle('is-on', t === tile); });
    var no = sec.querySelector('[data-kv-sbc-no]');
    if (no) no.textContent = tile.getAttribute('data-no') || '';
  }
  function from(e) {
    var tile = e.target.closest && e.target.closest('[data-kv-sbc-tile]');
    if (tile) activate(tile);
  }
  document.addEventListener('pointerover', from);
  document.addEventListener('focusin', from);
})();

/* ============================================================
   Collection grid: every product on one page. Shopify serves 50
   products per request, so the remaining pages are fetched with
   section rendering (same filters and sort) and their cards are
   appended in page order. The numbered pagination is hidden; it
   only remains for browsers without JavaScript.
   ============================================================ */
(function () {
  'use strict';

  var CONCURRENCY = 4;

  function loadAll(grid) {
    if (grid.dataset.kvAllReady) return;
    grid.dataset.kvAllReady = '1';
    var sid = grid.getAttribute('data-kv-section');
    var page = Number(grid.getAttribute('data-kv-page')) || 1;
    var pages = Number(grid.getAttribute('data-kv-pages')) || 1;
    var nav = document.querySelector('[data-kv-pag="' + sid + '"]');
    if (pages <= page) return;
    if (nav) nav.hidden = true;

    var urls = [];
    for (var p = page + 1; p <= pages; p++) {
      var u = new URL(window.location.href);
      u.hash = '';
      u.searchParams.set('page', String(p));
      u.searchParams.set('section_id', sid);
      urls.push(u.toString());
    }
    var results = new Array(urls.length);
    var next = 0;
    var shown = 0;
    var failed = false;
    grid.setAttribute('aria-busy', 'true');

    function flush() {
      while (shown < results.length && results[shown] !== undefined) {
        var cards = results[shown];
        results[shown] = null;
        shown++;
        if (!cards) continue;
        var frag = document.createDocumentFragment();
        cards.forEach(function (c) { frag.appendChild(document.importNode(c, true)); });
        var favs = frag.querySelectorAll('[data-kv-fav]');
        grid.appendChild(frag);
        if (window.kvWishlist) {
          Array.prototype.forEach.call(favs, function (b) {
            var on = window.kvWishlist.ids().indexOf(b.getAttribute('data-kv-fav')) > -1;
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
            var label = b.getAttribute(on ? 'data-label-on' : 'data-label-off');
            if (label) { b.setAttribute('aria-label', label); b.setAttribute('title', label); }
          });
        }
      }
      if (shown === results.length) {
        grid.removeAttribute('aria-busy');
        // a page that could not load: show the numbered links again so nothing is out of reach
        if (failed && nav) nav.hidden = false;
      }
    }

    function work() {
      if (next >= urls.length) return Promise.resolve();
      var i = next++;
      return fetch(urls[i], { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var g = doc.getElementById('KvProductGrid-' + sid);
          results[i] = g ? Array.prototype.slice.call(g.children) : [];
        })
        .catch(function () { results[i] = false; failed = true; })
        .then(function () { flush(); return work(); });
    }

    for (var k = 0; k < Math.min(CONCURRENCY, urls.length); k++) work();
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-kv-all]'), loadAll);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ============================================================
   Filter / Sort bar (.kv-tb): it always sticks under the compact
   header; "is-stuck" while it does. When the header slides away the
   stuck bar slides up with it by transform (kevlts.css), on the same
   timing, so both move as one piece without re-laying out the page
   on every frame. Stuck = the block above it (its place in the page)
   has scrolled under the header line. Looked up per frame because
   filtering re-renders the bar.
   ============================================================ */
(function () {
  'use strict';
  var ticking = false;
  var line = 0;
  function readLine() {
    line = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--kv-hdr-h-compact')) || 0;
  }
  function update() {
    ticking = false;
    Array.prototype.forEach.call(document.querySelectorAll('.kv-tb'), function (bar) {
      var above = bar.previousElementSibling;
      var place = above ? above.getBoundingClientRect().bottom : bar.parentElement.getBoundingClientRect().top;
      var stuck = place <= line + 0.5;
      if (stuck !== bar.classList.contains('is-stuck')) bar.classList.toggle('is-stuck', stuck);
    });
  }
  function queue() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', function () { readLine(); queue(); });
  function start() { readLine(); update(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
